// This file handles the inline store preview settings sidebar and keeps the preview iframe in sync with the current settings.

// Use React tools we need for the preview helpers.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

// Reuse the shared inline-store preview hook 
import { usePodInline } from "global/hooks/usePodInline";

// Import components
import { ColorPickerSmall as ColourPickerSmall } from "global/components/ColourPicker";
import { InfoButton } from "global/components/InfoButton";
import { PortalPopover } from "global/components/PortalPopover";
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";
import CreateButton from "global/components/CreateButton";
import RenderImageUploader from "global/components/ImageUploader"
import ToggleSwitch from "global/components/ToggleSwitch";
import { useToast } from "global/hooks/useToast";

// Import font manager helpers
import { LoadGoogleFont } from "global/utilities/fonts/FontManager";


// This component owns the full inline-store preview experience:
// - the preview iframe
// - the left-hand preview settings accordion
// - the helper logic that keeps the iframe in sync while the user edits settings

// Keep the portal preview aligned with the shared Strich theme colour variable names.
const LIVE_THEME_COLOUR_VARIABLE_MAP = {
    style_primary_colour: "--strich-colour-primary",
    style_primary_content_colour: "--strich-colour-primary-content",
    style_secondary_colour: "--strich-colour-secondary",
    style_secondary_content_colour: "--strich-colour-secondary-content",
    style_accent_colour: "--strich-colour-accent",
    style_accent_content_colour: "--strich-colour-accent-content",
    style_base_100_colour: "--strich-colour-base-100",
    style_base_100_content_colour: "--strich-colour-base-100-content",
    style_header_colour: "--strich-style-header-colour",
    style_header_content_colour: "--strich-style-header-content-colour",
    style_footer_colour: "--strich-style-footer-colour",
    style_footer_content_colour: "--strich-style-footer-content-colour",
    style_footer_info_colour: "--strich-colour-footer-info",
    style_footer_info_content_colour: "--strich-colour-footer-info-content",
    style_base_200_colour: "--strich-colour-base-200",
    style_base_200_content_colour: "--strich-colour-base-200-content",
    style_base_300_colour: "--strich-colour-base-300",
    style_base_300_content_colour: "--strich-colour-base-300-content",
    style_search_background_colour: "--strich-colour-search-background",
    style_search_text_colour: "--strich-colour-search-text",
    style_search_border_colour: "--strich-colour-search-border",
    style_search_focus_border_colour: "--strich-colour-search-focus-border",
    style_cart_modal_background_colour: "--strich-colour-cart-modal",
    style_cart_modal_text_colour: "--strich-colour-cart-modal-content",
    style_checkout_left_panel_colour: "--strich-colour-checkout-left-panel",
    style_checkout_left_panel_text_colour: "--strich-colour-checkout-left-panel-content",
    style_checkout_left_panel_border_colour: "--strich-colour-checkout-left-panel-border",
    style_checkout_right_panel_colour: "--strich-colour-checkout-right-panel",
    style_checkout_right_panel_text_colour: "--strich-colour-checkout-right-panel-content",
    style_checkout_right_panel_border_colour: "--strich-colour-checkout-right-panel-border",
    style_checkout_panel_background_colour: "--strich-colour-checkout-panel",
    style_checkout_panel_text_colour: "--strich-colour-checkout-panel-content",
    style_checkout_panel_border_colour: "--strich-colour-checkout-panel-border",
    style_inline_card_background_colour: "--strich-colour-inline-card",
    style_inline_card_text_colour: "--strich-colour-inline-card-content",
    style_inline_card_border_colour: "--strich-colour-inline-card-border",
    style_inline_add_to_cart_background_colour: "--strich-colour-inline-add-to-cart",
    style_inline_add_to_cart_text_colour: "--strich-colour-inline-add-to-cart-content",
    style_inline_add_to_cart_border_colour: "--strich-colour-inline-add-to-cart-border",
};

// Keep editable search sizing values separate because they need CSS length validation rather than colour validation.
const LIVE_THEME_SIZE_VARIABLE_MAP = {
    style_search_border_radius: "--strich-search-border-radius",
};

// Boolean visual effects are translated into CSS-ready values for instant preview updates.
const LIVE_THEME_EFFECT_VARIABLE_MAP = {
    style_ambient_gallery_backdrop: "--strich-lightbox-ambient-backdrop-opacity",
};

// Match the shared storefront defaults so clearing a field keeps the preview stable.
const LIVE_THEME_COLOUR_DEFAULTS = {
    style_primary_colour: "#1f4fd8",
    style_primary_content_colour: "#ffffff",
    style_secondary_colour: "#334155",
    style_secondary_content_colour: "#ffffff",
    style_accent_colour: "#3b82f6",
    style_accent_content_colour: "#ffffff",
    style_base_100_colour: "#ffffff",
    style_base_100_content_colour: "#0f172a",
    style_header_colour: "#3b82f6",
    style_header_content_colour: "#ffffff",
    style_footer_colour: "#3b82f6",
    style_footer_content_colour: "#ffffff",
    style_footer_info_colour: "#3b82f6",
    style_footer_info_content_colour: "#ffffff",
    style_base_200_colour: "#07090a",
    style_base_200_content_colour: "#0f172a",
    style_base_300_colour: "#d1d1d1",
    style_base_300_content_colour: "#0f172a",
    style_search_background_colour: "",
    style_search_text_colour: "",
    style_search_border_colour: "",
    style_search_focus_border_colour: "",
    style_cart_modal_background_colour: "#f1f5f9",
    style_cart_modal_text_colour: "#0f172a",
    style_checkout_left_panel_colour: "#f1f5f9",
    style_checkout_left_panel_text_colour: "#0f172a",
    style_checkout_left_panel_border_colour: "",
    style_checkout_right_panel_colour: "#d1d1d1",
    style_checkout_right_panel_text_colour: "#0f172a",
    style_checkout_right_panel_border_colour: "",
    style_checkout_panel_background_colour: "#f1f5f9",
    style_checkout_panel_text_colour: "#0f172a",
    style_checkout_panel_border_colour: "#cbd5e1",
    // Inline-card colours are optional so existing stores retain their current card styling.
    style_inline_card_background_colour: "",
    style_inline_card_text_colour: "",
    style_inline_card_border_colour: "",
    style_inline_add_to_cart_background_colour: "",
    style_inline_add_to_cart_text_colour: "",
    style_inline_add_to_cart_border_colour: "",
};

const LIVE_THEME_SIZE_DEFAULTS = {
    style_search_border_radius: "9999px",
};

// Reuse one stable style element id so preview-only CSS overrides are updated instead of duplicated.
const LIVE_THEME_PREVIEW_STYLE_ID = "strich-live-preview-theme-overrides";

// Reuse one stable stylesheet link id for the heading font inside the iframe document.
const LIVE_THEME_HEADER_FONT_LINK_ID = "strich-live-preview-header-font";

// Reuse one stable stylesheet link id for the body font inside the iframe document.
const LIVE_THEME_BODY_FONT_LINK_ID = "strich-live-preview-body-font";

// Offer a curated list of Google Fonts so the sidebar can provide a searchable picker instead of a plain text field only.
const GOOGLE_FONT_FAMILIES = [
    "Aboreto", "Abril Fatface", "Acme", "Alegreya", "Alegreya Sans",
    "Alfa Slab One", "Allerta Stencil", "Amatic SC", "Anek Latin", "Anonymous Pro",
    "Anton", "Architects Daughter", "Archivo", "Archivo Black", "Archivo Narrow",
    "Arima", "Arvo", "Asap", "Assistant", "Asul",
    "Average", "Averia Serif Libre", "Azeret Mono", "Bai Jamjuree", "Baloo 2",
    "Bangers", "Barlow", "Barlow Condensed", "Barriecito", "Baskervville",
    "Be Vietnam Pro", "Bebas Neue", "Belanosima", "Bellefair", "Belleza",
    "BenchNine", "Berkshire Swash", "Bevan", "BioRhyme", "Bitter",
    "BIZ UDPGothic", "Black Ops One", "Bodoni Moda", "Bowlby One", "Bree Serif",
    "Bricolage Grotesque", "Bubblegum Sans", "Bungee", "Cabin", "Cabin Condensed",
    "Cairo", "Calligraffitti", "Cambo", "Candal", "Cantarell",
    "Cardo", "Carter One", "Catamaran", "Caudex", "Caveat",
    "Changa", "Chela One", "Chelsea Market", "Chivo", "Chivo Mono",
    "Chonburi", "Cinzel", "Cinzel Decorative", "Comfortaa", "Comic Neue",
    "Commissioner", "Cormorant", "Cormorant Garamond", "Cormorant Infant", "Cormorant SC",
    "Cormorant Unicase", "Cormorant Upright", "Courgette", "Cousine", "Crete Round",
    "Crimson Text", "Cutive Mono", "Dancing Script", "Days One", "Dela Gothic One",
    "Didact Gothic", "DM Sans", "DM Serif Display", "Domine", "Dosis",
    "EB Garamond", "Eczar", "El Messiri", "Electrolize", "Encode Sans",
    "Exo 2", "Expletus Sans", "Explora", "Fanwood Text", "Fauna One",
    "Federo", "Figtree", "Fira Code", "Fira Sans", "Fjalla One",
    "Fjord One", "Forum", "Francois One", "Fraunces", "Fredoka",
    "Gabarito", "Galada", "Gantari", "Gelasio", "Gemunu Libre",
    "Germania One", "Gilda Display", "Glegoo", "Gloria Hallelujah", "Goudy Bookletter 1911",
    "Great Vibes", "Grenze", "Gruppo", "Handlee", "Hanken Grotesk",
    "Harmattan", "Heebo", "Hepta Slab", "Hind", "Hind Madurai",
    "Hind Siliguri", "IBM Plex Mono", "IBM Plex Sans", "IBM Plex Sans Condensed", "IBM Plex Serif",
    "Imbue", "Inconsolata", "Indie Flower", "Instrument Serif", "Inter",
    "Istok Web", "Italiana", "Jaldi", "Josefin Sans", "Josefin Slab",
    "Jost", "Jura", "Kalam", "Kanit", "Karla",
    "Kaushan Script", "Kavoon", "Khand", "Kreon", "Krub",
    "Kufam", "Kumbh Sans", "Lateef", "Lato", "League Spartan",
    "Lemonada", "Lexend", "Libre Baskerville", "Libre Bodoni", "Libre Caslon Display",
    "Libre Caslon Text", "Libre Franklin", "Lilita One", "Literata", "Lobster",
    "Lora", "Luckiest Guy", "Lusitana", "M PLUS 1p", "M PLUS Rounded 1c",
    "Mada", "Maitree", "Manrope", "Marcellus", "Marcellus SC",
    "Markazi Text", "Marmelad", "Martel", "Maven Pro", "Merriweather",
    "Merriweather Sans", "Metrophobic", "Michroma", "Mitr", "Monda",
    "Montaga", "Montserrat", "Mukta", "Mulish", "Murecho",
    "Nanum Gothic", "Neuton", "Newsreader", "Nixie One", "Noticia Text",
    "Noto Sans", "Noto Serif", "Nunito", "Nunito Sans", "Old Standard TT",
    "Open Sans", "Open Sans Condensed", "Orbitron", "Oswald", "Outfit",
    "Overpass", "Oxygen", "Pacifico", "Padauk", "Parisienne",
    "Passion One", "Pathway Extreme", "Patrick Hand", "Permanent Marker", "Petrona",
    "Philosopher", "Play", "Playfair Display", "Plus Jakarta Sans", "Poiret One",
    "Poppins", "Prata", "Pridi", "Prompt", "PT Mono",
    "PT Sans", "PT Serif", "Public Sans", "Questrial", "Quicksand",
    "Radley", "Rajdhani", "Raleway", "Rasa", "Readex Pro",
    "Recursive", "Red Hat Display", "Red Hat Text", "Righteous", "Roboto",
    "Roboto Condensed", "Roboto Flex", "Roboto Slab", "Rokkitt", "Rowdies",
    "Rubik", "Rubik Dirt", "Rubik Gemstones", "Rubik Mono One", "Rubik Wet Paint",
    "Rufina", "Russo One", "Sacramento", "Saira", "Sancreek",
    "Sarabun", "Sarala", "Sarpanch", "Satisfy", "Sawarabi Mincho",
    "Secular One", "Sedgwick Ave", "Sen", "Shadows Into Light", "Shrikhand",
    "Signika", "Silkscreen", "Slabo 27px", "Smooch Sans", "Sofia Sans",
    "Sora", "Sorts Mill Goudy", "Source Sans 3", "Source Serif 4", "Space Grotesk",
    "Space Mono", "Special Elite", "Spectral", "Staatliches", "Stardos Stencil",
    "Stint Ultra Expanded", "Suez One", "Syne", "Tajawal", "Taviraj",
    "Teko", "Tenor Sans", "Tienne", "Tilt Neon", "Tilt Warp",
    "Tinos", "Titillium Web", "Trirong", "Ubuntu", "Ultra",
    "Unbounded", "Unna", "Urbanist", "Varela Round", "Vidaloka",
    "Viga", "Vollkorn", "Vollkorn SC", "Voltaire", "Work Sans",
    "Workbench", "Yanone Kaffeesatz", "Yeseva One", "Yrsa", "Zen Kaku Gothic New",
    "Zen Old Mincho", "Zeyada", "Zilla Slab",
];

// Normalise font inputs so we can accept either a plain family name or a Google Fonts URL in the same field.
function GetNormalizedFontFamilyName(value = "") {

    // Trim the incoming value so pasted whitespace behaves the same as an empty field.
    const safeValue = String(value ?? "").trim();

    // Stop here when the value is empty so we do not try to parse a URL.
    if (!safeValue) {

        // Return an empty string for empty values so the parent can clear the field.
        return "";
    }

    // Extract the readable family name from Google Fonts URLs so the picker can display/search it.
    if (/^https?:\/\//i.test(safeValue)) {

        try {
        
            // Parse the URL and extract the family query parameter, then normalise it to a readable font name.
            const parsedUrl = new URL(safeValue);

            // Stop here when the URL is not a Google Fonts URL so we do not try to parse it.
            const familyParam = parsedUrl.searchParams.get("family");

            // Return the first family name from the query parameter, replacing any + with spaces so it is readable.
            return familyParam ? familyParam.split(":")[0].replace(/\+/g, " ") : "";
        
        } catch {

            // Fall back safely when the pasted URL cannot be parsed.
            return "";
        }
    }

    // Plain font family names can pass straight through.
    return safeValue;

}


const GoogleFontPicker = ({
    value = "",
    placeholder = "",
    onChange,
    onOpen = null,
    onClose = null,
}) => {

    // Keep a ref directly on the input so the dropdown can position itself against it.
    const inputRef = useRef(null);

    // Keep a ref to the portalled dropdown list.
    const fontListRef = useRef(null);

    // Keep a ref for the currently selected option.
    const selectedOptionRef = useRef(null);

    // Store whether the dropdown is currently open.
    const [isOpen, setIsOpen] = useState(false);

    // Store the visible input/search value.
    const [searchValue, setSearchValue] = useState(() => GetNormalizedFontFamilyName(value));

    // Store whether the user has typed a search in this open session.
    const [hasUserTypedSearch, setHasUserTypedSearch] = useState(false);



    // Open the dropdown in one place.
    const openPicker = () => {

        // Reset filtering so opening the picker shows all fonts first.
        setHasUserTypedSearch(false);

        // Open the dropdown.
        setIsOpen(true);

        // Tell the parent this picker is open.
        if (typeof onOpen === "function") {

            // Call the parent open handler.
            onOpen();

        }

    };



    // Close the dropdown in one place.
    const closePicker = () => {

        // Close the dropdown.
        setIsOpen(false);

        // Tell the parent this picker is closed.
        if (typeof onClose === "function") {

            // Call the parent close handler.
            onClose();

        }

    };

    // Load the selected font when the saved value changes.
    useEffect(() => {

        // Load the current selected font.
        LoadGoogleFont(value);

    }, [value]);



    // Sync the input when the parent value changes.
    useEffect(() => {

        // Mirror parent value changes back into the input.
        setSearchValue(GetNormalizedFontFamilyName(value));

    }, [value]);



    // Filter the curated Google Fonts list against the live search string.
    const filteredFonts = useMemo(() => {

        // Normalise the search string.
        const normalizedQuery = String(searchValue || "").trim().toLowerCase();

        // Show all fonts when the picker first opens.
        if (!hasUserTypedSearch || !normalizedQuery) {

            // Return all fonts.
            return GOOGLE_FONT_FAMILIES;

        }

        // Return only matching fonts.
        return GOOGLE_FONT_FAMILIES.filter((fontFamily) => (

            // Match the search string against the font family name.
            fontFamily.toLowerCase().includes(normalizedQuery)

        ));

    }, [searchValue, hasUserTypedSearch]);



    // Load fonts around the visible part of the dropdown.
    const loadFontsAroundScrollPosition = useCallback(() => {

        // Stop if the dropdown is not rendered yet.
        if (!fontListRef.current) {

            // Exit early.
            return;

        }

        // Estimate the height of each option.
        const optionHeight = 38;

        // Read the current scroll position.
        const scrollTop = fontListRef.current.scrollTop;

        // Read the visible dropdown height.
        const visibleHeight = fontListRef.current.clientHeight;

        // Calculate the first visible option index.
        const firstVisibleIndex = Math.floor(scrollTop / optionHeight);

        // Calculate how many options fit in the visible area.
        const visibleCount = Math.ceil(visibleHeight / optionHeight);

        // Start loading a few fonts before the visible area.
        const startIndex = Math.max(0, firstVisibleIndex - 8);

        // Stop loading a few fonts after the visible area.
        const endIndex = Math.min(filteredFonts.length, firstVisibleIndex + visibleCount + 12);

        // Get the nearby fonts.
        const nearbyFonts = filteredFonts.slice(startIndex, endIndex);

        // Load each nearby font.
        nearbyFonts.forEach((fontFamily) => {

            // Request this Google Font.
            LoadGoogleFont(fontFamily);

        });

    }, [filteredFonts]);



    // When the picker opens, scroll the selected font into view and load nearby previews.
    useEffect(() => {

        // Do nothing while the picker is closed.
        if (!isOpen) {

            // Exit early.
            return;

        }

        // Wait until the portalled dropdown has rendered.
        requestAnimationFrame(() => {

            // Scroll the selected font into the centre of the dropdown if it exists.
            selectedOptionRef.current?.scrollIntoView({

                // Centre the selected option.
                block: "center",

            });

            // Wait one more frame so scrollTop has updated after scrollIntoView.
            requestAnimationFrame(() => {

                // Load fonts around the visible scroll position.
                loadFontsAroundScrollPosition();

            });

        });

    }, [isOpen, filteredFonts, value, loadFontsAroundScrollPosition]);



    // Apply the chosen font and close the dropdown.
    const applyFontValue = (nextValue) => {

        // Normalise the chosen value.
        const normalizedValue = String(nextValue ?? "").trim();

        // Update the input immediately.
        setSearchValue(normalizedValue);

        // Reset the search state.
        setHasUserTypedSearch(false);

        // Load the selected font immediately.
        LoadGoogleFont(normalizedValue);

        // Notify the parent.
        onChange?.(normalizedValue);

        // Close the picker.
        closePicker();

    };



    // Render the picker.
    return (

        <div className="store-theme-ribbon__fontPicker">

            <input
                ref={inputRef}
                className="store-theme-ribbon__input store-theme-ribbon__fontPickerInput"
                type="text"
                value={searchValue}
                placeholder={placeholder}
                style={{
                    fontFamily: searchValue ? `"${searchValue}", sans-serif` : undefined,
                }}
                onFocus={openPicker}
                onClick={openPicker}
                onChange={(event) => {

                    // Update the visible input value.
                    setSearchValue(event.target.value);

                    // Mark that the user is actively searching.
                    setHasUserTypedSearch(true);

                    // Keep the dropdown open while typing.
                    setIsOpen(true);

                }}
            />

            <PortalPopover
                anchorRef={inputRef}
                isOpen={isOpen}
                onClose={closePicker}
                className="store-theme-ribbon__fontPickerPopover store-theme-ribbon__fontPickerPopover--portal"
                offset={6}
                innerRef={fontListRef}
                onScroll={loadFontsAroundScrollPosition}
            >

                {searchValue.trim() && !GOOGLE_FONT_FAMILIES.some((fontFamily) => fontFamily.toLowerCase() === searchValue.trim().toLowerCase()) && (

                    <button
                        type="button"
                        className="store-theme-ribbon__fontPickerOption store-theme-ribbon__fontPickerOption--custom"
                        onMouseDown={(event) => {

                            // Prevent the input from losing focus before click runs.
                            event.preventDefault();

                        }}
                        onClick={() => applyFontValue(searchValue)}
                    >

                        Use "{searchValue.trim()}"

                    </button>

                )}

                {filteredFonts.map((fontFamily) => {

                    // Check whether this option is the currently selected font.
                    const isSelectedFont = fontFamily.toLowerCase() === String(value || "").trim().toLowerCase();

                    // Return one font option.
                    return (

                        <button
                            key={fontFamily}
                            ref={isSelectedFont ? selectedOptionRef : null}
                            type="button"
                            className={`store-theme-ribbon__fontPickerOption ${
                                isSelectedFont ? "store-theme-ribbon__fontPickerOption--selected" : ""
                            }`}
                            style={{
                                fontFamily: `"${fontFamily}", sans-serif`,
                            }}
                            onMouseDown={(event) => {

                                // Prevent the input from losing focus before click runs.
                                event.preventDefault();

                            }}
                            onClick={() => applyFontValue(fontFamily)}
                        >

                            {fontFamily}

                        </button>

                    );

                })}

                {!filteredFonts.length && (

                    <div className="store-theme-ribbon__fontPickerEmpty">

                        Press the custom option above to use this font.

                    </div>

                )}

            </PortalPopover>

        </div>

    );

};

// These groups decide which settings appear together inside each accordion section.
const THEME_RIBBON_GROUPS = [
    {
        key: "palette",
        label: "Palette",
        settings: [
            "style_primary_colour",
            "style_primary_content_colour",
            "style_secondary_colour",
            "style_secondary_content_colour",
            "style_accent_colour",
            "style_accent_content_colour",
            "style_use_accent_colour",
        ],
    },
    {
        key: "surfaces",
        label: "Layout",
        settings: [
            "style_base_100_colour",
            "style_base_100_content_colour",
            "style_header_colour",
            "style_header_content_colour",
            "style_footer_colour",
            "style_footer_content_colour",
            "style_footer_info_colour",
            "style_footer_info_content_colour",
            "style_base_200_colour",
            "style_base_200_content_colour",
            "style_base_300_colour",
            "style_base_300_content_colour",
        ],
    },
    {
        key: "cart_checkout",
        label: "Cart & Checkout",
        settings: [
            "style_cart_modal_background_colour",
            "style_cart_modal_text_colour",
            "style_checkout_left_panel_colour",
            "style_checkout_left_panel_text_colour",
            "style_checkout_left_panel_border_colour",
            "style_checkout_right_panel_colour",
            "style_checkout_right_panel_text_colour",
            "style_checkout_right_panel_border_colour",
            "style_checkout_panel_background_colour",
            "style_checkout_panel_text_colour",
            "style_checkout_panel_border_colour",
        ],
    },
    {
        key: "search",
        label: "Search",
        settings: [
            "style_search_background_colour",
            "style_search_text_colour",
            "style_search_border_colour",
            "style_search_focus_border_colour",
            "style_search_border_radius",
        ],
    },
    {
        key: "type",
        label: "Typeface",
        settings: [
            "style_header_font",
            "style_body_font",
        ],
    },
    {
        key: "inline_cards",
        label: "Inline Products",
        settings: [
            "style_inline_card_background_colour",
            "style_inline_card_text_colour",
            "style_inline_card_border_colour",
            "style_inline_add_to_cart_background_colour",
            "style_inline_add_to_cart_text_colour",
            "style_inline_add_to_cart_border_colour",
        ],
    },
    {
        key: "cart",
        label: "Cart Settings",
        settings: [
            "cart_basket",
        ],
    },
    {
        key: "top_menu",
        label: "Top Menu",
        settings: [
            "store_top_menu_pages",
        ],
    },
    {
        key: "images",
        label: "Images",
        settings: [
            "style_brand_logo_url",
            "style_brand_logo_alignment",
            "style_ambient_gallery_backdrop",
        ],
    },
];

// Pair surface colours with their matching content colours so the editor can
// show one compact, representative card for each colour role.
const GROUPED_COLOUR_PAIRS = {
    palette: [
        {
            key: "primary",
            label: "Primary",
            backgroundCode: "style_primary_colour",
            textCode: "style_primary_content_colour",
        },
        {
            key: "secondary",
            label: "Secondary",
            backgroundCode: "style_secondary_colour",
            textCode: "style_secondary_content_colour",
        },
        {
            key: "accent",
            label: "Accent",
            backgroundCode: "style_accent_colour",
            textCode: "style_accent_content_colour",
        },
    ],
    surfaces: [
        {
            key: "header",
            label: "Header",
            backgroundCode: "style_header_colour",
            textCode: "style_header_content_colour",
        },
        {
            key: "main",
            label: "Main",
            backgroundCode: "style_base_100_colour",
            textCode: "style_base_100_content_colour",
        },
        {
            key: "footer-info",
            label: "Footer Info",
            backgroundCode: "style_footer_info_colour",
            textCode: "style_footer_info_content_colour",
        },
        {
            key: "footer",
            label: "Footer",
            backgroundCode: "style_footer_colour",
            textCode: "style_footer_content_colour",
        },
    ],
    cart_checkout: [
        {
            key: "cart-modal",
            label: "Cart Modal",
            backgroundCode: "style_cart_modal_background_colour",
            textCode: "style_cart_modal_text_colour",
        },
        {
            key: "checkout-left",
            label: "Checkout Left",
            backgroundCode: "style_checkout_left_panel_colour",
            textCode: "style_checkout_left_panel_text_colour",
            borderCode: "style_checkout_left_panel_border_colour",
        },
        {
            key: "checkout-right",
            label: "Checkout Right",
            backgroundCode: "style_checkout_right_panel_colour",
            textCode: "style_checkout_right_panel_text_colour",
            borderCode: "style_checkout_right_panel_border_colour",
        },
        {
            key: "checkout-panels",
            label: "Checkout Panels",
            backgroundCode: "style_checkout_panel_background_colour",
            textCode: "style_checkout_panel_text_colour",
            borderCode: "style_checkout_panel_border_colour",
        },
    ],
    search: [
        {
            key: "search",
            label: "Search",
            backgroundCode: "style_search_background_colour",
            textCode: "style_search_text_colour",
            borderCode: "style_search_border_colour",
            focusBorderCode: "style_search_focus_border_colour",
        },
    ],
    inline_cards: [
        {
            key: "inline-cards",
            label: "Product Cards",
            backgroundCode: "style_inline_card_background_colour",
            textCode: "style_inline_card_text_colour",
            borderCode: "style_inline_card_border_colour",
        },
        {
            key: "inline-add-to-cart",
            label: "Add to Cart",
            backgroundCode: "style_inline_add_to_cart_background_colour",
            textCode: "style_inline_add_to_cart_text_colour",
            borderCode: "style_inline_add_to_cart_border_colour",
        },
    ],
};

// Keep paired settings out of the ordinary one-setting-per-row renderer.
const GROUPED_COLOUR_SETTING_CODES = new Set(
    [
        ...Object.values(GROUPED_COLOUR_PAIRS)
            .flat()
            .flatMap(({ backgroundCode, textCode, borderCode, focusBorderCode }) => (
                [backgroundCode, textCode, borderCode, focusBorderCode].filter(Boolean)
            )),
        // Retain these legacy values for existing stores, but no longer expose their unused controls.
        "style_base_200_colour",
        "style_base_200_content_colour",
        "style_base_300_colour",
        "style_base_300_content_colour",
    ]
);

// These sections group styling, cart and navigation controls under Theme, leaving image controls under Images.
const THEME_SIDEBAR_SECTIONS = [
    {
        key: "theme",
        title: "",
        description: "",
        groups: ["palette", "surfaces", "cart_checkout", "inline_cards", "search", "type", "cart", "top_menu"],
    },
    {
        key: "images",
        title: "",
        description: "",
        groups: ["images"],
    },
];

// Keep the styling sidebar focused on theme controls and images.
const THEME_SIDEBAR_TABS = [
    {
        key: "theme",
        title: "Theme",
        sectionKeys: ["theme"],
    },
    {
        key: "images",
        title: "Images",
        sectionKeys: ["images"],
    },
];

// These definitions drive the editable controls:
// each setting knows its label, help text, input type, and optional default/select options.
const THEME_RIBBON_SETTING_DEFINITIONS = {
    store_top_menu_pages: {
        type: "Store",
        value_type: "text",
        name: "Top Menu Pages",
        description: "Choose up to four existing pages to show after Products in the desktop and mobile navigation.",
        shortLabel: "Pages",
        inputMode: "page-multi-select",
        defaultValue: "",
    },
    style_primary_colour: {
        type: "Style",
        name: "Primary Colour",
        description: "Your main brand colour for buttons and key actions.",
        shortLabel: "Primary Colour",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_primary_colour,
    },
    style_primary_content_colour: {
        type: "Style",
        name: "Primary Content Colour",
        description: "Text and icon colour shown on top of your primary colour.",
        shortLabel: "Primary Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_primary_content_colour,
    },
    style_secondary_colour: {
        type: "Style",
        name: "Secondary Colour",
        description: "A second brand colour for supporting buttons and actions.",
        shortLabel: "Secondary Colour",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_secondary_colour,
    },
    style_secondary_content_colour: {
        type: "Style",
        name: "Secondary Content Colour",
        description: "Text and icon colour shown on top of your secondary colour.",
        shortLabel: "Secondary Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_secondary_content_colour,
    },
    style_accent_colour: {
        type: "Style",
        name: "Accent Colour",
        description: "Highlight colour for selected options, progress, and active states.",
        shortLabel: "Accent Colour",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_accent_colour,
    },
    style_accent_content_colour: {
        type: "Style",
        name: "Accent Content Colour",
        description: "Text and icon colour shown on top of your accent colour.",
        shortLabel: "Accent Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_accent_content_colour,
    },
    pod_inline_preview_background: {
        type: "POD Inline",
        name: "Preview Background",
        description: "Background colour behind the live preview in the portal.",
        shortLabel: "Preview Background",
        inputMode: "colour",
        defaultValue: "#ffffff",
    },
    style_base_100_colour: {
        type: "Style",
        name: "Base 100 Colour",
        description: "Main page background colour.",
        shortLabel: "Main Background",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_base_100_colour,
    },
    style_base_100_content_colour: {
        type: "Style",
        name: "Base 100 Content Colour",
        description: "Main text colour used on your main background.",
        shortLabel: "Main Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_base_100_content_colour,
    },


    style_header_colour: {
        type: "Style",
        name: "Header Colour",
        description: "Header colour at the top of the store.",
        shortLabel: "Header Background",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_header_colour,
    },
    style_header_content_colour: {
        type: "Style",
        name: "Header Content Colour",
        description: "Text colour used on the header.",
        shortLabel: "Header Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_header_content_colour,
    },
    style_footer_colour: {
        type: "Style",
        name: "Footer Colour",
        description: "Footer colour at the bottom of the store.",
        shortLabel: "Footer Background",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_footer_colour,
    },
    style_footer_content_colour: {
        type: "Style",
        name: "Footer Content Colour",
        description: "Text colour used on the footer.",
        shortLabel: "Footer Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_footer_content_colour,
    },
    style_footer_info_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Footer Info Colour",
        description: "Background colour for the information-rich upper footer.",
        shortLabel: "Footer Info Background",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_footer_info_colour,
    },
    style_footer_info_content_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Footer Info Content Colour",
        description: "Text colour for the information-rich upper footer.",
        shortLabel: "Footer Info Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_footer_info_content_colour,
    },


    style_base_200_colour: {
        type: "Style",
        name: "Base 200 Colour",
        description: "Background colour for softer panels, modals, and dropdowns.",
        shortLabel: "Soft Panels",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_base_200_colour,
    },
    style_base_200_content_colour: {
        type: "Style",
        name: "Base 200 Content Colour",
        description: "Text colour used on softer panels and surfaces.",
        shortLabel: "Soft Panel Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_base_200_content_colour,
    },
    style_base_300_colour: {
        type: "Style",
        name: "Base 300 Colour",
        description: "Stronger surface colour for cards, borders, dividers, and inputs.",
        shortLabel: "Cards & Borders",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_base_300_colour,
    },
    style_base_300_content_colour: {
        type: "Style",
        name: "Base 300 Content Colour",
        description: "Text colour used on stronger surface areas.",
        shortLabel: "Cards & Borders Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_base_300_content_colour,
    },
    style_search_background_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Search Background Colour",
        description: "Background colour for the store product search box. Leave unset to inherit the existing input surface colour.",
        shortLabel: "Search Background",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_search_background_colour,
    },
    style_search_text_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Search Text Colour",
        description: "Text and placeholder colour used inside the store product search box. Leave unset to inherit the existing input text colour.",
        shortLabel: "Search Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_search_text_colour,
    },
    style_search_border_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Search Border Colour",
        description: "Border colour for the store product search box. Leave unset to inherit the existing input border colour.",
        shortLabel: "Search Border",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_search_border_colour,
    },
    style_search_focus_border_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Search Focus Border Colour",
        description: "Border and focus-ring colour shown when the store product search box is active. Leave unset to inherit the accent colour.",
        shortLabel: "Focus Border",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_search_focus_border_colour,
    },
    style_search_border_radius: {
        type: "Style",
        value_type: "text",
        name: "Search Border Radius",
        description: "Corner radius for the store product search box, from square to fully rounded.",
        shortLabel: "Border Radius",
        inputMode: "range",
        min: 0,
        max: 2,
        step: 0.1,
        unit: "rem",
        defaultValue: "2rem",
    },
    style_cart_modal_background_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Cart Modal Background Colour",
        description: "Background colour for the cart modal and checkout-complete modal.",
        shortLabel: "Cart Modal Background",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_cart_modal_background_colour,
    },
    style_cart_modal_text_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Cart Modal Text Colour",
        description: "Text colour for the cart modal and checkout-complete modal.",
        shortLabel: "Cart Modal Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_cart_modal_text_colour,
    },
    style_checkout_left_panel_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Checkout Left Panel Colour",
        description: "Background colour for the customer and payment details panel in checkout.",
        shortLabel: "Checkout Left Panel",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_checkout_left_panel_colour,
    },
    style_checkout_left_panel_text_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Checkout Left Panel Text Colour",
        description: "Text colour for the customer and payment details panel in checkout.",
        shortLabel: "Checkout Left Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_checkout_left_panel_text_colour,
    },
    style_checkout_left_panel_border_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Checkout Left Panel Border Colour",
        description: "Border colour for the customer and payment details column in checkout.",
        shortLabel: "Checkout Left Border",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_checkout_left_panel_border_colour,
    },
    style_checkout_right_panel_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Checkout Right Panel Colour",
        description: "Background colour for the order summary panel in checkout.",
        shortLabel: "Checkout Right Panel",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_checkout_right_panel_colour,
    },
    style_checkout_right_panel_text_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Checkout Right Panel Text Colour",
        description: "Text colour for the order summary panel in checkout.",
        shortLabel: "Checkout Right Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_checkout_right_panel_text_colour,
    },
    style_checkout_right_panel_border_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Checkout Right Panel Border Colour",
        description: "Border colour for the order summary column in checkout.",
        shortLabel: "Checkout Right Border",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_checkout_right_panel_border_colour,
    },
    style_checkout_panel_background_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Checkout Panel Background Colour",
        description: "Background colour for the Shipping Details, Shipping Options and Payment Method panels.",
        shortLabel: "Checkout Panel Background",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_checkout_panel_background_colour,
    },
    style_checkout_panel_text_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Checkout Panel Text Colour",
        description: "Text colour for the Shipping Details, Shipping Options and Payment Method panels.",
        shortLabel: "Checkout Panel Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_checkout_panel_text_colour,
    },
    style_checkout_panel_border_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Checkout Panel Border Colour",
        description: "Border colour for the Shipping Details, Shipping Options and Payment Method panels.",
        shortLabel: "Checkout Panel Border",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_checkout_panel_border_colour,
    },
    style_inline_card_background_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Inline Card Background Colour",
        description: "Background colour for cards in the inline product grid.",
        shortLabel: "Inline Card Background",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_inline_card_background_colour,
    },
    style_inline_card_text_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Inline Card Text Colour",
        description: "Text colour for cards in the inline product grid.",
        shortLabel: "Inline Card Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_inline_card_text_colour,
    },
    style_inline_card_border_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Inline Card Border Colour",
        description: "Border colour for cards in the inline product grid.",
        shortLabel: "Inline Card Border",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_inline_card_border_colour,
    },
    style_inline_add_to_cart_background_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Inline Add to Cart Background Colour",
        description: "Background colour for Add to Cart buttons on inline product cards.",
        shortLabel: "Add to Cart Background",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_inline_add_to_cart_background_colour,
    },
    style_inline_add_to_cart_text_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Inline Add to Cart Text Colour",
        description: "Text colour for Add to Cart buttons on inline product cards.",
        shortLabel: "Add to Cart Text",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_inline_add_to_cart_text_colour,
    },
    style_inline_add_to_cart_border_colour: {
        type: "Style",
        value_type: "hex_colour",
        name: "Inline Add to Cart Border Colour",
        description: "Border colour for Add to Cart buttons on inline product cards.",
        shortLabel: "Add to Cart Border",
        inputMode: "colour",
        defaultValue: LIVE_THEME_COLOUR_DEFAULTS.style_inline_add_to_cart_border_colour,
    },
    style_use_accent_colour: {
        type: "Style",
        value_type: "yn",
        name: "Use Accent Colour",
        description: "Use the separate Accent Colour, or reuse Primary Colour for accent elements.",
        shortLabel: "Use Accent Colour",
        inputMode: "toggle",
        defaultValue: "N",
    },
    style_header_font: {
        type: "Style",
        name: "Header Font",
        description: "Font used for headings and titles. Search Google Fonts or type your own font family.",
        shortLabel: "Header Font",
        inputMode: "font",
        placeholder: "Inter",
        defaultValue: "",
    },
    style_body_font: {
        type: "Style",
        name: "Body Font",
        description: "Font used for body text and interface text. Search Google Fonts or type your own font family.",
        shortLabel: "Body Font",
        inputMode: "font",
        placeholder: "Source Sans 3",
        defaultValue: "",
    },
    pod_inline_template: {
        type: "POD Inline",
        name: "Template",
        description: "Default template code to use for this inline store.",
        shortLabel: "Template",
        inputMode: "text",
        placeholder: "select...",
        defaultValue: "select",
    },
    pod_inline_title: {
        type: "POD Inline",
        name: "Storefront Title",
        description: "Main heading shown at the top of your inline store.",
        shortLabel: "Storefront Title",
        inputMode: "text",
        placeholder: "Bring The Artwork Home",
        defaultValue: "",
    },
    pod_inline_subtitle: {
        type: "POD Inline",
        name: "Storefront Subtitle",
        description: "Smaller line of text shown above the storefront title.",
        shortLabel: "Storefront Subtitle",
        inputMode: "text",
        placeholder: "Select a painting, then choose from the available products, formats, and finishes.",
        defaultValue: "",
    },
    pod_inline_product_count: {
        type: "POD Inline",
        name: "Show Product Count",
        description: "Show or hide the product count above the inline products.",
        shortLabel: "Show Product Count",
        inputMode: "select",
        defaultValue: "N",
        options: [
            { value: "Y", label: "Show" },
            { value: "N", label: "Hide" },
        ],
    },
    pod_inline_products_position: {
        type: "POD Inline",
        name: "Products Position / Alignment",
        description: "Choose whether products align left, centre, or right.",
        shortLabel: "Products Alignment",
        inputMode: "select",
        defaultValue: "left",
        options: [
            { value: "left", label: "Left" },
            { value: "center", label: "Center" },
            { value: "right", label: "Right" },
        ],
    },
    pod_inline_view_style: {
        type: "POD Inline",
        name: "View Style",
        description: "Choose which product view style this inline store should use.",
        shortLabel: "View Style",
        inputMode: "text",
        placeholder: "default",
        defaultValue: "",
    },
    pod_inline_fit_fill: {
        type: "POD Inline",
        name: "Fit / Fill",
        description: "Choose the default image fitting style for products in your store.",
        shortLabel: "Fit / Fill",
        inputMode: "select",
        defaultValue: "fit",
        options: [
            { value: "fit", label: "Fit" },
            { value: "fill", label: "Fill" },
        ],
    },
    pod_inline_fit_fill_toggle: {
        type: "POD Inline",
        name: "Fit / Fill Toggle",
        description: "Show or hide the fit / fill switch on the inline store.",
        shortLabel: "Fit / Fill Toggle",
        inputMode: "select",
        defaultValue: "Y",
        options: [
            { value: "Y", label: "Show" },
            { value: "N", label: "Hide" },
        ],
    },
    pod_inline_adjust: {
        type: "POD Inline",
        name: "Adjust Position",
        description: "Turn on image position adjustment in the inline store.",
        shortLabel: "Adjust Position",
        inputMode: "select",
        defaultValue: "N",
        options: [
            { value: "Y", label: "Enable" },
            { value: "N", label: "Disable" },
        ],
    },
    pod_inline_floating: {
        type: "POD Inline",
        name: "Floating Products",
        description: "Show products in a popup from the image button instead of on the page.",
        shortLabel: "Floating Products",
        inputMode: "select",
        defaultValue: "N",
        options: [
            { value: "Y", label: "Enable" },
            { value: "N", label: "Disable" },
        ],
    },
    pod_inline_floating_button_text: {
        type: "POD Inline",
        name: "Floating Button Text",
        description: "Text shown on the floating products button.",
        shortLabel: "Floating Button Text",
        inputMode: "text",
        placeholder: "Shop Products",
        defaultValue: "",
    },
    pod_inline_floating_button_left: {
        type: "POD Inline",
        name: "Floating Button From Left %",
        description: "How far from the left the floating button should appear.",
        shortLabel: "Button From Left %",
        inputMode: "text",
        placeholder: "50",
        defaultValue: "",
    },
    pod_inline_floating_button_top: {
        type: "POD Inline",
        name: "Floating Button From Top %",
        description: "How far from the top the floating button should appear.",
        shortLabel: "Button From Top %",
        inputMode: "text",
        placeholder: "50",
        defaultValue: "",
    },
    pod_inline_country_currency_dropdown: {
        type: "POD Inline",
        name: "Country / Currency Dropdown",
        description: "Show or hide the country and currency dropdowns above the products.",
        shortLabel: "Country / Currency",
        inputMode: "select",
        defaultValue: "Y",
        options: [
            { value: "Y", label: "Show" },
            { value: "N", label: "Hide" },
        ],
    },
    pod_inline_separate_child_products: {
        type: "POD Inline",
        name: "Separate Child Products",
        description: "Show child products as separate items instead of using a dropdown.",
        shortLabel: "Separate Child Products",
        inputMode: "select",
        defaultValue: "N",
        options: [
            { value: "Y", label: "Show Separately" },
            { value: "N", label: "Use Dropdown" },
        ],
    },
    pod_inline_top_gallery: {
        type: "POD Inline",
        name: "Top Gallery",
        description: "Show or hide the image gallery at the top of the inline store.",
        shortLabel: "Top Gallery",
        inputMode: "select",
        defaultValue: "N",
        options: [
            { value: "Y", label: "Show" },
            { value: "N", label: "Hide" },
        ],
    },
    cart_basket: {
        type: "Cart",
        name: "Cart Basket",
        description: "Show or hide the mini cart basket for this store.",
        shortLabel: "Cart Basket",
        inputMode: "select",
        defaultValue: "Y",
        options: [
            { value: "Y", label: "Show" },
            { value: "N", label: "Hide" },
        ],
    },
    style_brand_logo_url: {
        type: "Images",
        name: "Logo",
        description: "Your brand logo to display on store, invoices and related materials.",
        shortLabel: "Logo",
        inputMode: "image",
        defaultValue: "",
    },
    style_brand_logo_alignment: {
        type: "Style",
        value_type: "text",
        name: "Logo Alignment",
        description: "Position the store header logo on the left or in the centre.",
        shortLabel: "Logo Alignment",
        inputMode: "select",
        defaultValue: "centre",
        options: [
            { value: "left", label: "Left" },
            { value: "centre", label: "Centre" },
        ],
    },
    style_ambient_gallery_backdrop: {
        type: "Style",
        value_type: "yn",
        name: "Ambient Gallery Backdrop",
        description: "Use a blurred version of the current gallery image behind the lightbox preview.",
        shortLabel: "Ambient Gallery Backdrop",
        inputMode: "toggle",
        defaultValue: "N",
    },
};

// Reuse one CSS.supports() validator for any live-preview CSS property checks in this file.
const isSupportedLivePreviewCssValue = (property, value) => {

    // Trim incoming values so empty strings and whitespace-only values fail fast.
    const safeValue = String(value ?? "").trim();

    // Stop here when the value is empty so we do not try to validate it as CSS.
    if (!safeValue) {
        return false;
    }

    // Use the browser's CSS.supports() API to check whether the value is valid for the given property.
    return typeof window !== "undefined"
        && !!window.CSS
        && typeof window.CSS.supports === "function"
        && window.CSS.supports(property, safeValue);

};


// Read one setting value from the array by its Strich code.
const GetSettingValueByCode = (settings = [], code = "", defaultValue = "") => {
    
    // Search the array for the requested Strich setting code.
    const matchingSetting = Array.isArray(settings)
        ? settings.find((setting) => setting?.code === code)
        : null;

    // Fall back when the setting is missing or does not carry an explicit value key.
    if (!matchingSetting || !Object.prototype.hasOwnProperty.call(matchingSetting, "value")) {
        return defaultValue;
    }

    // Return the stored raw setting value when found.
    return matchingSetting.value;

};

// Read the ordered page slugs stored by the Top Menu multi-select.
const GetTopMenuPageSlugs = (value = "") => {
    return Array.from(new Set(
        String(value || "")
            .split(",")
            .map((slug) => String(slug || "").trim().toLowerCase())
            .filter(Boolean)
    )).slice(0, 4);
};

// Merge saved settings with draft settings so preview helpers can read one current snapshot by code.
const MergeSettingsByCode = (savedSettings = [], draftSettings = []) => {

    // Use a map so later draft settings overwrite earlier saved settings by code.
    const mergedSettingsByCode = new Map();

    // Add saved settings first so they can be overwritten by draft settings when the same code exists in both.
    (Array.isArray(savedSettings) ? savedSettings : []).forEach((setting) => {

        // Normalise the code before using it as the map key.
        const safeCode = String(setting?.code || "").trim();

        // Stop here when the code is empty so we do not pollute the map with invalid keys.
        if (safeCode) {
            // Add the saved setting to the map so it can be overwritten by a draft setting with the same code.
            mergedSettingsByCode.set(safeCode, setting);
        }

    });

    // Add draft settings next so they can overwrite saved settings when the same code exists in both.
    (Array.isArray(draftSettings) ? draftSettings : []).forEach((setting) => {

        // Draft values win when they share the same code as a saved value.
        const safeCode = String(setting?.code || "").trim();

        // Stop here when the code is empty so we do not pollute the map with invalid keys.
        if (safeCode) {
            // Add the draft setting to the map so it can overwrite a saved setting with the same code.
            mergedSettingsByCode.set(safeCode, setting);
        }

    });

    // Return one merged settings snapshot that preview helpers can read consistently.
    return Array.from(mergedSettingsByCode.values());

};

// Find the preview theme roots inside the iframe so live CSS variable updates can be applied everywhere.
const GetLivePreviewThemeTargets = (previewDocument) => {

    // Stop here when the preview document is missing so we do not try to query it.
    if (!previewDocument) {
        // Return an empty array so the caller can safely skip any updates.
        return [];
    }

    // Collect every known theme root the Strich storefront, floating modal, and cart runtime may render into.
    const previewTargets = [
        previewDocument.documentElement,
        previewDocument.body,
        ...Array.from(
            previewDocument.querySelectorAll(
                [
                    "#strich-podinline-root",
                    "#strich_custom_modal.strich_podinline_floating_modal",
                    ".strich_podinline_storefront",
                    ".strich_cart_theme",
                    ".strich_store_theme",
                    ".strich_podinline_theme",
                ].join(", ")
            )
        ),
    ].filter(Boolean);

    // Dedupe nodes so repeated selectors do not cause duplicate style writes.
    return Array.from(new Set(previewTargets));

};

// Update image fit/fill classes without forcing a full iframe rebuild.
const ApplyLivePreviewFitFill = (previewDocument, fitFillValue = "fit") => {

    // Stop here when the preview document is missing so we do not try to query it.
    if (!previewDocument) {
        return;
    }

    // Find both the grid tiles and gallery preview images that share the fit/fill classes.
    const previewProductImages = previewDocument.querySelectorAll(
        ".product-results-product__background-image, .product-variations__gallery-item-image"
    );

    // Remove any existing fit/fill classes and add the new one based on the current setting value.
    previewProductImages.forEach((imageEl) => {
        imageEl.classList.remove("product-results-product__background-image--fit", "product-results-product__background-image--fill");
        imageEl.classList.add(
            fitFillValue === "fill"
                ? "product-results-product__background-image--fill"
                : "product-results-product__background-image--fit"
        );
    });

};

// Show or hide the fit/fill control inside the live preview.
const ApplyLivePreviewFitFillToggleVisibility = (previewDocument, toggleSettingValue = "Y") => {

    // Stop here when the preview document is missing so we do not try to query it.
    if (!previewDocument) {
        return;
    }

    // Decide whether the toggle should be visible based on the current setting value.
    const shouldShowToggle = toggleSettingValue === "Y";

    // Find the controls container that wraps the fit/fill toggle so we can show or hide it.
    const previewControls = previewDocument.querySelector(".strich_podinline_controls");

    // Show or hide all known fit/fill toggle elements inside the preview so the user sees the correct state.
    previewDocument
        .querySelectorAll(".product-results-toolbar__fitfill, .pod-inline-fitfill-toggle, .strich_podinline_fitfill")
        .forEach((toggleEl) => {
            toggleEl.style.display = shouldShowToggle ? "" : "none";
        });

    // Show or hide the controls container itself so the layout does not leave an empty gap when the toggle is hidden.
    if (previewControls) {
        
        // Use flex display so the controls container can still align other elements when the toggle is hidden.
        previewControls.style.display = shouldShowToggle ? "flex" : "none";

    }

};

// These settings are treated as structural preview identity settings because older runtimes cannot always update them safely in place.
const PREVIEW_FULL_REFRESH_SETTING_CODES = [
    "pod_inline_floating",
    "pod_inline_product_count",
    "pod_inline_fit_fill",
    "pod_inline_fit_fill_toggle",
    "pod_inline_country_currency_dropdown",
    "pod_inline_separate_child_products",
    "cart_basket",
];

// These child settings only matter when floating products are enabled, so the UI reveals them conditionally.
const POD_INLINE_FLOATING_SUB_SETTING_CODES = [
    "pod_inline_floating_button_text",
    "pod_inline_floating_button_left",
    "pod_inline_floating_button_top",
];

// Validate sizing values before pushing them into CSS custom properties in the iframe.
const IsLiveThemeCssSizeValue = (value) => {

    // Trim incoming values so empty strings and whitespace-only values fail fast.
    const safeValue = String(value ?? "").trim();

    // Stop here when the value is empty so we do not try to validate it as CSS.
    if (!safeValue) {
        return false;
    }

    // Use the browser's CSS.supports() API to check whether the value is valid for common CSS properties.
    return typeof window !== "undefined"
        && !!window.CSS
        && typeof window.CSS.supports === "function"
        && (
            window.CSS.supports("font-size", safeValue)
            || window.CSS.supports("border-radius", safeValue)
            || window.CSS.supports("margin", safeValue)
        );

};

// Convert legacy pixel values and current rem values into the 0–2rem slider range.
const GetSearchBorderRadiusSliderValue = (value) => {

    const safeValue = String(value ?? "").trim().toLowerCase();
    const match = safeValue.match(/^(-?(?:\d+\.?\d*|\.\d+))(px|rem)?$/);

    if (!match) {
        return 2;
    }

    const rawNumber = Number(match[1]);
    const radiusInRem = match[2] === "px" ? rawNumber / 16 : rawNumber;

    if (!Number.isFinite(radiusInRem)) {
        return 2;
    }

    return Math.min(2, Math.max(0, radiusInRem));

};

// Guard logo updates so only proper web URLs are pushed into the preview iframe.
// Used for both the logo URL and the optional Google Fonts URL so we do not inject unsafe protocols.
const IsLiveThemeUrlValue = (value) => {

    // Trim incoming values so empty strings and whitespace-only values fail fast.
    const safeValue = String(value ?? "").trim();

    // Stop here when the value is empty so we do not try to validate it as a URL.
    if (!safeValue) {
        return false;
    }

    try {
    
        // Parse the value as a URL and check that it uses either HTTP or HTTPS so we do not inject unsafe protocols.
        const parsedUrl = new URL(safeValue);

        // Return true only when the URL uses a safe protocol that the browser can load inside an iframe.
        return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
    
    } catch {
        
        // Fall back safely when the value cannot be parsed as a URL.
        return false;

    }

};

// Inject or update the required Google Fonts stylesheet inside the preview iframe document.
const EnsureLivePreviewFontStylesheet = (previewDocument, linkId, fontValue) => {

    // Stop here when the preview document is missing so we do not try to query it.
    if (!previewDocument) {
        return;
    }

    // Return early if font is null
    if (!fontValue) { 
        return; 
    }

    // Read the raw font value and try to reuse an existing stylesheet link when possible.
    const safeFontValue = String(fontValue ?? "").trim();
    
    // Find the existing link element by its ID so we can update or remove it as needed.
    let linkEl = previewDocument.getElementById(linkId);

    // Remove the existing font stylesheet when the setting has been cleared.
    if (!safeFontValue) {

        // Remove the existing font stylesheet when the setting has been cleared.
        if (linkEl) {

            // Remove the link element from the document so the font is no longer loaded.
            linkEl.remove();
        
        }
        return;
    }

    // Accept either a full stylesheet URL or a plain font family name that we convert into a Google Fonts URL.
    const href = /^https?:\/\//i.test(safeFontValue)
        ? safeFontValue
        : `https://fonts.googleapis.com/css2?family=${encodeURIComponent(safeFontValue).replace(/%20/g, "+")}:wght@400;500;600;700&display=swap`;

    // No existing link was found, so create one and append it to the head of the preview document.
    if (!linkEl) {

        // Create the link lazily the first time this font is needed inside the iframe.
        linkEl = previewDocument.createElement("link");

        // Assign the ID and rel attributes so the link can be found and treated as a stylesheet.
        linkEl.id = linkId;

        // Set the rel attribute to "stylesheet" so the browser knows to load it as a CSS file.
        linkEl.rel = "stylesheet";

        // Append the link to the head so the browser can load the font stylesheet.
        previewDocument.head.appendChild(linkEl);

    }

    // Update the href only when it has actually changed to avoid unnecessary reloads.
    if (linkEl.getAttribute("href") !== href) {

        // Update the href attribute to point to the new font stylesheet URL so the browser can load it.
        linkEl.setAttribute("href", href);

    }

};

// Add one shared style block to the iframe so preview-only font and spacing overrides can work.
const EnsureLivePreviewThemeStyleElement = (previewDocument) => {

    // Stop here when the preview document is missing so we do not try to query it.
    if (!previewDocument) {
        return null;
    }

    // Reuse one shared style tag so live preview-only CSS overrides stay centralised.
    let styleEl = previewDocument.getElementById(LIVE_THEME_PREVIEW_STYLE_ID);

    if (!styleEl) {

        // Create the style element lazily the first time preview overrides are needed.
        styleEl = previewDocument.createElement("style");
        
        // Assign the ID so the style element can be found and updated later.
        styleEl.id = LIVE_THEME_PREVIEW_STYLE_ID;

        // Append the style element to the head so the browser can apply its CSS rules.
        previewDocument.head.appendChild(styleEl);

    }

    // Update the style element's content with the latest CSS rules that use the live preview variables.
    styleEl.textContent = `
        .strich_podinline_storefront_header h1,
        .strich_podinline_storefront_intro h1,
        .strich_podinline_storefront_intro h2,
        .strich_podinline_storefront_intro h3,
        .strich_podinline_storefront_products h1,
        .strich_podinline_storefront_products h2,
        .strich_podinline_storefront_products h3 {
            font-family: var(--strich-preview-header-font, inherit);
        }

        .strich_podinline_storefront_header,
        .strich_podinline_storefront_intro,
        .strich_podinline_storefront_products,
        .strich_cart_theme,
        .strich_store_theme {
            font-family: var(--strich-preview-body-font, inherit);
            font-size: var(--strich-preview-base-font-size, inherit);
        }

        .strich_podinline_storefront_products,
        .strich_podinline_storefront_intro,
        .strich_cart_theme,
        .strich_store_theme {
            --strich-preview-spacing-active: var(--strich-preview-spacing, 1rem);
            --strich-preview-border-radius-active: var(--strich-preview-border-radius, 12px);
        }

        .strich_podinline_storefront_products .product-results-product,
        .strich_podinline_storefront_intro .product-results-product,
        .strich_cart_theme .button,
        .strich_store_theme .button,
        .strich_podinline_storefront_intro .button,
        .strich_podinline_storefront_products .button {
            border-radius: var(--strich-preview-border-radius-active);
        }

        #strich_checkout_modal #cart_checkout > div:first-child > .strich_cart_fieldset,
        #strich_checkout_modal .strich_cart_checkout_form_column > .strich_cart_fieldset {
            background: var(--strich-colour-checkout-panel, var(--strich-colour-base-300, #d1d1d1)) !important;
            color: var(--strich-colour-checkout-panel-content, var(--strich-colour-base-300-content, #0f172a)) !important;
            border-color: var(--strich-colour-checkout-panel-border, currentColor) !important;
        }

        #strich_checkout_modal #cart_checkout > div:first-child > .strich_cart_fieldset .strich_cart_legend,
        #strich_checkout_modal #cart_checkout > div:first-child > .strich_cart_fieldset .strich_cart_label,
        #strich_checkout_modal .strich_cart_checkout_form_column > .strich_cart_fieldset .strich_cart_legend,
        #strich_checkout_modal .strich_cart_checkout_form_column > .strich_cart_fieldset .strich_cart_label {
            color: var(--strich-colour-checkout-panel-content, var(--strich-colour-base-300-content, #0f172a)) !important;
        }
    `;

    // Return the style element so the caller can update it later if needed.
    return styleEl;

};

// Mirror editable storefront heading copy directly into the iframe as the user types.
const SyncLivePreviewStorefrontText = (previewDocument, options = {}) => {

    // Stop here when the preview document is missing so we do not try to query it.
    if (!previewDocument) {
        return;
    }

    // Trim the next title strings before pushing them into the iframe.
    const titleText = String(options?.title ?? "").trim();

    // Trim the next subtitle strings before pushing them into the iframe.
    const subtitleText = String(options?.subtitle ?? "").trim();

    // Update every mirrored title marker inside the preview and hide empty placeholders.
    previewDocument.querySelectorAll("[data-strich-preview-storefront-title]").forEach((titleEl) => {

        // Update the text content of the title element to match the current setting value.
        titleEl.textContent = titleText;

        // Hide the title element when the text is empty so the layout does not leave a gap.
        titleEl.style.display = titleText ? "" : "none";

    });

    // Do the same for the mirrored subtitle markers.
    previewDocument.querySelectorAll("[data-strich-preview-storefront-subtitle]").forEach((subtitleEl) => {

        // Update the text content of the subtitle element to match the current setting value.
        subtitleEl.textContent = subtitleText;

        // Hide the subtitle element when the text is empty so the layout does not leave a gap.
        subtitleEl.style.display = subtitleText ? "" : "none";
    
    });

};

// Keep the floating trigger button text in sync with the matching preview setting.
const SyncLivePreviewFloatingButtonText = (previewDocument, nextText = "") => {

    // Stop here when the preview document is missing so we do not try to query it.
    if (!previewDocument) {
        return;
    }

    // Trim the next label value so blank strings behave consistently.
    const safeText = String(nextText ?? "").trim();

    // Update every known floating trigger label element used by newer and older preview builds.
    previewDocument
        .querySelectorAll(".pod-floating-trigger span, .pod-floating-trigger-label")
        .forEach((labelEl) => {
            labelEl.textContent = safeText;
        });

};



const InlineStorePreviewSettings = ({
    isOpen,
    addingNewConnection,
    connectionType = "",
    savedSettings = [],
    formSettings = [],
    previewAlias = "",
    availablePodTemplates = [],
    availablePages = [],
    handleSettingsChange,
    handleSettingValueChange,
}) => {

    // Only STRICH stores support the embedded inline preview experience.
    const showPodInlinePreview = String(connectionType || "").trim().toUpperCase() === "STRICH";

    // Track which top-level tab is currently active.
    const [activePreviewSidebarTab, setActivePreviewSidebarTab] = useState("theme");

    // Track which sidebar accordion groups are currently expanded.
    const [openPreviewSidebarSections, setOpenPreviewSidebarSections] = useState({});

    // Once the current iframe instance has rendered, keep it visible during live in-place updates.
    const [hasPreviewRenderedOnce, setHasPreviewRenderedOnce] = useState(false);

    // Store which tab is currently selected for display preview type: all, cart, product, checkout, store home page, store product page, etc. (Mirror the iframe panel expansion state so the portal controls stay in sync)
    const [expandedPreviewPanel, setExpandedPreviewPanel] = useState("view_all");

    // Store which colour picker is currently open
    const [openColourPickerCode, setOpenColourPickerCode] = useState(null);

    // Store which popover is currently open
    const [openFieldCode, setOpenFieldCode] = useState(null);

    // Keep the drag identity in a ref for event handlers and state for clear visual feedback.
    const draggedTopMenuPageSlugRef = useRef("");
    const lastTopMenuDragEndedAtRef = useRef(0);
    const [draggedTopMenuPageSlug, setDraggedTopMenuPageSlug] = useState("");
    const [topMenuDropTargetSlug, setTopMenuDropTargetSlug] = useState("");

    // Use the shared toast so preview state changes have a clear explanation.
    const { toastInfo } = useToast();

    // Offer each current page once; Home appears only when the user explicitly selects it.
    const availableTopMenuPages = useMemo(() => {
        const pagesBySlug = new Map();

        (Array.isArray(availablePages) ? availablePages : []).forEach((page) => {
            const slug = String(page?.slug || "").trim().toLowerCase();

            if (!slug || page?.change_type === "deleted" || pagesBySlug.has(slug)) {
                return;
            }

            pagesBySlug.set(slug, {
                slug,
                name: String(page?.name || slug).trim() || slug,
            });
        });

        return Array.from(pagesBySlug.values());
    }, [availablePages]);

    // Remember the last POD Inline-only settings batch sent to the runtime so colour edits do not trigger unrelated product rerenders.
    const lastPodInlineRuntimeSettingsKeyRef = useRef("");


    // Some settings cannot be updated reliably in-place, so we pass only those into the hook
    // to trigger a full iframe rebuild when they change.
    const previewBootstrapSettings = useMemo(() => {

        // Keep only the draft settings that should force the iframe identity to change.
        const draftSettings = Array.isArray(formSettings)
            ? formSettings.filter((setting) => PREVIEW_FULL_REFRESH_SETTING_CODES.includes(setting?.code))
            : [];

        // Merge those draft values over the saved values so rebuild-triggering settings still respect unsaved edits.
        return MergeSettingsByCode(savedSettings, draftSettings);

    }, [savedSettings, formSettings]);



    // Full iframe rebuilds should still use the latest unsaved draft values for colours, copy, and typography.
    const previewRenderSettings = useMemo(() => (
        MergeSettingsByCode(savedSettings, formSettings)
    ), [savedSettings, formSettings]);



    // The shared hook builds the iframe HTML and returns the loading / ready state for the preview.
    const {
        previewFrameRef,
        previewSrcDoc,
        previewKey,
        isLoading: podInlineIsLoading,
        error: podInlineError,
        status: podInlineStatus,
        previewMessageType,
    } = usePodInline({
        enabled: isOpen && showPodInlinePreview && !addingNewConnection && Boolean(previewAlias),
        alias: previewAlias,
        settings: previewRenderSettings,
        previewIdentitySettings: previewBootstrapSettings,
    });



    useEffect(() => {

        // Every time the preview key changes, treat the next iframe load as a brand new preview instance.
        setHasPreviewRenderedOnce(false);

        // Reset the expanded panel state so the next iframe can open its own panels without interference.
        setExpandedPreviewPanel("view_all");

        // Let the rebuilt iframe receive one fresh POD Inline settings batch once its runtime is ready.
        lastPodInlineRuntimeSettingsKeyRef.current = "";
        
    }, [previewKey]);



    useEffect(() => {

        // Once the active iframe reports ready, keep it visible during later live in-place updates.
        if (podInlineStatus === "ready") {
            setHasPreviewRenderedOnce(true);
        }
    }, [podInlineStatus]);



    // Only show the blocking loading state before the current preview instance has rendered once.
    const showPreviewLoadingState = !hasPreviewRenderedOnce && (!previewSrcDoc || podInlineIsLoading || podInlineStatus === "loading");



    // Build the left-hand accordion fields by merging saved values with the current draft values.
    const themeRibbonSettings = useMemo(() => (

        // Map the field groups into the higher-level ribbon groups shown in the UI.
        THEME_RIBBON_GROUPS.map((group) => ({
            ...group,
            fields: group.settings.map((code) => {

                // Look up the field definition by its setting code so we can merge it with the current value.
                const definition = THEME_RIBBON_SETTING_DEFINITIONS[code] || null;

                // Use the current draft value when available, otherwise fall back to the saved value or the default.
                const currentValue = GetSettingValueByCode(
                    formSettings,
                    code,
                    GetSettingValueByCode(
                        savedSettings,
                        code,
                        definition?.defaultValue || ""
                    )
                );

                // Some settings need to be resolved into a different input mode or options list based on the current state of the account.
                let resolvedDefinition = definition;

                if (code === "pod_inline_template") {

                    // Build the template dropdown from live account templates so the preview can use real template choices.
                    const templateOptions = [

                        { value: null, label: "Select..." },

                        ...availablePodTemplates.map((template) => {

                            // Use the English translation when available, otherwise fall back to the template title or code.
                            const englishTranslation = Array.isArray(template?.translations)
                                ? template.translations.find((translation) => translation?.language === "EN")
                                : null;

                            // Trim the template code so empty strings do not appear in the dropdown.
                            const templateCode = String(template?.code || "").trim();

                            // Trim the template title so empty strings do not appear in the dropdown.
                            const templateTitle = String(
                                englishTranslation?.title
                                || template?.title
                                || templateCode
                                || "Untitled Template"
                            ).trim();

                            // Only include templates that have a valid code so the dropdown does not show empty options.
                            return {
                                value: templateCode,
                                label: templateTitle,
                            };
                        }).filter((option) => option.value),

                    ];


                    // Check whether the current template code is present in the fetched template list so we can keep it visible if it is missing (don't include the null 'Select...' option)
                    const hasCurrentTemplateOption = templateOptions.some((option) => option.value === null || String(currentValue || "").trim());

                    // Keep the current template code visible even if it is no longer present in the fetched template list.
                    resolvedDefinition = 
                        availablePodTemplates.length === 0 ? {
                            ...definition,
                            inputMode: "setting-message",
                            disabled: true,
                            placeholder: "You need to create a template before you can apply it.",
                            
                        } 
                        
                        :

                    {
                        ...definition,
                        inputMode: "select",
                        options: hasCurrentTemplateOption || !String(currentValue || "").trim()
                            ? templateOptions
                            : [
                                ...templateOptions,
                                {
                                    value: String(currentValue).trim(),
                                    label: `${String(currentValue).trim()} (Current)`,
                                },
                            ],
                    };
                }

                // Return the merged field definition with the current value so the UI can render it correctly.
                return {
                    ...resolvedDefinition,
                    code,
                    value: currentValue,
                };
            }),
        }))
    ), [availablePodTemplates, formSettings, savedSettings]);



    // Map the field groups into the higher-level sidebar sections shown in the UI.
    const previewSidebarSections = useMemo(() => (
        // Expand each sidebar section with the resolved ribbon groups it should display.
        THEME_SIDEBAR_SECTIONS.map((section) => ({
            ...section,
            // Keep only the ribbon groups whose keys belong to this section.
            groups: themeRibbonSettings.filter((group) => section.groups.includes(group.key)),
        }))
    ), [themeRibbonSettings]);



    // Map the sidebar sections into the top-level tabs shown above the settings list.
    const previewSidebarTabs = useMemo(() => (
        // Expand each tab with the sidebar sections it should render.
        THEME_SIDEBAR_TABS.map((tab) => ({
            ...tab,
            // Keep only the sections whose keys belong to this tab.
            sections: previewSidebarSections.filter((section) => tab.sectionKeys.includes(section.key)),
        }))
    ), [previewSidebarSections]);



    // Wrap the shared text/select change handler so help popovers close before a field updates.
    const createSettingsChangeHandler = (...args) => {

        // Build the original upstream change handler once using the provided field metadata.
        const upstreamChangeHandler = handleSettingsChange(...args);

        return (event) => {

            // Forward the original DOM event into the shared upstream change handler.
            return upstreamChangeHandler(event);

        };

    };



    // Do the same for value-only controls such as colour pickers and the custom font picker.
    const createSettingValueChangeHandler = useCallback((...args) => {

        // Build the original upstream value-change handler once using the provided field metadata.
        const upstreamValueChangeHandler = handleSettingValueChange(...args);

        return (value) => {

            // Forward the next value into the shared upstream value-change handler.
            return upstreamValueChangeHandler(value);

        };

    }, [handleSettingValueChange]);



    // Send toolbar actions into the iframe so the portal controls can open and close preview panels remotely.
    const sendPreviewPanelAction = (action, panelKey = "") => {

        // Stop when the active iframe window is not available yet.
        if (!previewFrameRef.current?.contentWindow) {
            return;
        }

        // Reuse the shared preview message channel and preview id so only the active iframe responds.
        previewFrameRef.current.contentWindow.postMessage({
            type: previewMessageType,
            previewId: previewKey,
            action,
            panelKey,
        }, "*");

    };

    // Font changes apply across the store, not only to the Cart or Checkout panel.
    // Return to the full preview first so the edit has a meaningful visible result.
    const minimiseCartPreviewForFontEditing = useCallback(() => {
        if (!["cart", "checkout"].includes(expandedPreviewPanel)) {
            return;
        }

        if (previewFrameRef.current?.contentWindow) {
            previewFrameRef.current.contentWindow.postMessage({
                type: previewMessageType,
                previewId: previewKey,
                action: "close-expanded-panel",
                panelKey: "",
            }, "*");
        }
        setExpandedPreviewPanel("view_all");
        toastInfo({
            title: "Preview minimised",
            message: "Can't edit checkout font while Cart preview is maximised.",
            autoCloseMs: 4000,
        });
    }, [expandedPreviewPanel, previewFrameRef, previewKey, previewMessageType, toastInfo]);


    
    useEffect(() => {

        // Stop here when inline preview mode is not active so we do not attach unnecessary global listeners.
        if (!showPodInlinePreview) {
            return;
        }

        // Listen for iframe-originated actions so the portal toolbar and settings can stay in sync with preview interactions.
        const handlePreviewActionMessage = (event) => {

            // Ignore messages that did not come from the active preview iframe window.
            if (event.source !== previewFrameRef.current?.contentWindow) {

                return;

            }
            
            // Read the posted message payload once so the checks below stay consistent.
            const message = event.data;

            // Ignore unrelated message types so only preview-control events are processed here.
            if (message?.type !== previewMessageType) {

                return;

            }

            // Ignore messages for older preview instances so stale iframes cannot update current state.
            if (message?.previewId !== previewKey) {

                return;

            }

            // Mirror the preview panel state back into the portal toolbar when the iframe reports a change.
            if (message?.action === "expanded-panel-changed") {

                // Check for empty panel key
                if (message?.panelKey === "") {

                    // If no panel key set it to 'view all'
                    setExpandedPreviewPanel("view_all");

                } else {

                    // Mirror the expanded panel name so the toolbar buttons reflect the iframe state.
                    setExpandedPreviewPanel(String(message?.panelKey || "").trim().toLowerCase());

                }

                return;
            }

            // Save background changes made from the compact colour control in the Product preview header.
            if (message?.action === "update-setting" && message?.settingCode === "pod_inline_preview_background") {
                const nextPreviewBackground = String(message?.settingValue || "").trim();

                if (/^#[0-9a-f]{6}$/i.test(nextPreviewBackground)) {
                    createSettingValueChangeHandler(
                        "POD Inline",
                        "pod_inline_preview_background",
                        "Preview Background",
                        "Background colour behind the live preview in the portal.",
                        "hex_colour"
                    )(nextPreviewBackground);
                }

                return;
            }

            // Ignore any message that is not the specific cart-basket enable request supported here.
            if (message?.action !== "enable-setting" || message?.settingCode !== "cart_basket") {
                return;
            }

            // Allow the preview runtime to request that cart basket is enabled when a preview flow depends on it.
            createSettingValueChangeHandler(
                "POD Inline",
                "cart_basket",
                "Cart Basket",
                "Show or hide the mini cart basket for this store.",
                "yn"
            )("Y");
        };

        // Attach the global message listener while this preview is active.
        window.addEventListener("message", handlePreviewActionMessage);

        return () => {
            // Remove the global listener when the preview state changes or unmounts.
            window.removeEventListener("message", handlePreviewActionMessage);
        };

    }, [createSettingValueChangeHandler, previewFrameRef, previewKey, previewMessageType, showPodInlinePreview]);



    // Initialise accordion state the first time the preview opens: default the first group in each section open, but preserve any manual user toggles afterwards.
    useEffect(() => {

        // Stop here until the preview sidebar is actually visible and relevant to the current connection.
        if (!isOpen || !showPodInlinePreview) {
            return;
        }

        // Seed or preserve the accordion open state so each section keeps one sensible default open group.
        setOpenPreviewSidebarSections((previousSections) => {
            // Build the next accordion-state object from the currently available sidebar sections.
            const nextSections = {};

            // Walk each section so we can decide which child groups should be open by default.
            previewSidebarSections.forEach((section) => {
                // Walk each group and default only the first group in each section to open.
                section.groups.forEach((group, index) => {
                    // Reuse the stable group key for the accordion-state map.
                    const sectionGroupKey = group.key;

                    // Preserve any user-toggled state, otherwise default the first group in the section to open.
                    nextSections[sectionGroupKey] = Object.prototype.hasOwnProperty.call(previousSections, sectionGroupKey)
                        ? previousSections[sectionGroupKey]
                        : index === 0;
                });
            });

            // Collect the previous keys so we can avoid pointless state updates when nothing changed.
            const previousKeys = Object.keys(previousSections);
            // Collect the next keys for the same shallow equality check.
            const nextKeys = Object.keys(nextSections);

            // Reuse the previous object when the accordion state is unchanged to avoid extra rerenders.
            if (
                previousKeys.length === nextKeys.length
                && nextKeys.every((key) => previousSections[key] === nextSections[key])
            ) {
                return previousSections;
            }

            // Commit the new accordion-state snapshot when the available groups or defaults changed.
            return nextSections;
        });

    }, [isOpen, showPodInlinePreview, previewSidebarSections]);



    useEffect(() => {

        // Stop here when the inline preview is not active so tab state does not update unnecessarily.
        if (!showPodInlinePreview) {
            return;
        }

        // If the available tabs ever change, fall back to the default Theme tab when the current tab disappears.
        if (!previewSidebarTabs.some((tab) => tab.key === activePreviewSidebarTab)) {
            setActivePreviewSidebarTab("theme");
        }

    }, [activePreviewSidebarTab, previewSidebarTabs, showPodInlinePreview]);



    // Mirror editable theme and POD Inline settings into the live iframe preview as the user types.
    useEffect(() => {

        // Stop here until the preview is open, usable, and associated with an existing saved store.
        if (!isOpen || addingNewConnection || !showPodInlinePreview) {
            return;
        }

        // Read the active iframe document so live DOM updates can be pushed into the preview.
        const previewDocument = previewFrameRef.current?.contentDocument;
        // Read the active iframe window so runtime updaters can be called when available.
        const previewWindow = previewFrameRef.current?.contentWindow;

        // Stop here when the iframe document has not been created yet.
        if (!previewDocument) {
            return;
        }

        // Read one merged settings snapshot so cart / theme / storefront helpers all work from the same source.
        const mergedPreviewSettings = MergeSettingsByCode(
            savedSettings,
            formSettings,
        );

        // Find every relevant preview DOM root that needs theme variables and live style updates.
        const previewTargets = GetLivePreviewThemeTargets(previewDocument);

        // Keep the exact live colour variables together so downstream cart and hosted-store bridges receive the same snapshot.
        const liveThemeCssVariables = {};

        if (!previewTargets.length) {
            return;
        }

        // Apply colour settings as CSS custom properties so the preview updates instantly.
        Object.entries(LIVE_THEME_COLOUR_VARIABLE_MAP).forEach(([settingCode, cssVariable]) => {

            // Read the current unsaved colour value for this setting code.
            const currentValue = String(
                GetSettingValueByCode(formSettings, settingCode, "")
            ).trim();

            // Only swap the preview when the draft colour is valid; otherwise keep the last good preview value.
            if (currentValue) {
                if (!isSupportedLivePreviewCssValue("color", currentValue)) {
                    return;
                }

                // Push the valid draft colour into every known preview theme root.
                previewTargets.forEach((target) => {
                    target.style.setProperty(cssVariable, currentValue);
                });

                // Preserve this valid draft value when a child runtime performs its own theme refresh.
                liveThemeCssVariables[cssVariable] = currentValue;

                return;
            }

            // Fall back to the saved colour value when there is no draft colour to apply.
            const savedValue = String(
                GetSettingValueByCode(savedSettings, settingCode, LIVE_THEME_COLOUR_DEFAULTS[settingCode] || "")
            ).trim();

            // Reuse the saved colour only when it is valid, otherwise fall back to the hardcoded default.
            const fallbackValue = isSupportedLivePreviewCssValue("color", savedValue)
                ? savedValue
                : LIVE_THEME_COLOUR_DEFAULTS[settingCode] || "";

            // Push the saved-or-default colour into every known preview theme root.
            previewTargets.forEach((target) => {
                target.style.setProperty(cssVariable, fallbackValue);
            });

            // Pass the resolved fallback through the same runtime bridge as live draft colours.
            if (fallbackValue) {
                liveThemeCssVariables[cssVariable] = fallbackValue;
            }

        });

        // When a store opts out of a separate accent colour, reuse Primary and its content colour everywhere.
        const usesAccentColour = String(
            GetSettingValueByCode(
                formSettings,
                "style_use_accent_colour",
                GetSettingValueByCode(savedSettings, "style_use_accent_colour", "N")
            )
        ).trim().toUpperCase() === "Y";

        if (!usesAccentColour) {
            const primaryColour = liveThemeCssVariables["--strich-colour-primary"]
                || LIVE_THEME_COLOUR_DEFAULTS.style_primary_colour;
            const primaryContentColour = liveThemeCssVariables["--strich-colour-primary-content"]
                || LIVE_THEME_COLOUR_DEFAULTS.style_primary_content_colour;

            previewTargets.forEach((target) => {
                target.style.setProperty("--strich-colour-accent", primaryColour);
                target.style.setProperty("--strich-colour-accent-content", primaryContentColour);
            });

            liveThemeCssVariables["--strich-colour-accent"] = primaryColour;
            liveThemeCssVariables["--strich-colour-accent-content"] = primaryContentColour;
        }

        // Apply validated search sizing settings and forward them through the same live runtime bridge as colours.
        Object.entries(LIVE_THEME_SIZE_VARIABLE_MAP).forEach(([settingCode, cssVariable]) => {
            const currentValue = String(
                GetSettingValueByCode(
                    formSettings,
                    settingCode,
                    GetSettingValueByCode(savedSettings, settingCode, LIVE_THEME_SIZE_DEFAULTS[settingCode] || "")
                )
            ).trim();
            const fallbackValue = LIVE_THEME_SIZE_DEFAULTS[settingCode] || "";
            const resolvedValue = IsLiveThemeCssSizeValue(currentValue) ? currentValue : fallbackValue;

            previewTargets.forEach((target) => {
                target.style.setProperty(cssVariable, resolvedValue);
            });

            if (resolvedValue) {
                liveThemeCssVariables[cssVariable] = resolvedValue;
            }
        });

        // Ensure the shared preview-only style element exists before we update its related CSS variables.
        EnsureLivePreviewThemeStyleElement(previewDocument);

        // Keep preview typography in sync with the editable font fields.
        // Read the current header-font setting so the preview can update its heading typography.
        const headerFontValue = GetSettingValueByCode(
            formSettings,
            "style_header_font",
            GetSettingValueByCode(savedSettings, "style_header_font", "")
        );

        // Read the current body-font setting so the preview can update its overall typography.
        const bodyFontValue = GetSettingValueByCode(
            formSettings,
            "style_body_font",
            GetSettingValueByCode(savedSettings, "style_body_font", "")
        );

        // Convert the raw header font setting into a usable CSS font-family value.
        const resolvedHeaderFont = GetNormalizedFontFamilyName(headerFontValue);
        // Convert the raw body font setting into a usable CSS font-family value.
        const resolvedBodyFont = GetNormalizedFontFamilyName(bodyFontValue);

        // Ensure the matching Google Fonts stylesheet exists for the current header font.
        EnsureLivePreviewFontStylesheet(previewDocument, LIVE_THEME_HEADER_FONT_LINK_ID, headerFontValue);
        // Ensure the matching Google Fonts stylesheet exists for the current body font.
        EnsureLivePreviewFontStylesheet(previewDocument, LIVE_THEME_BODY_FONT_LINK_ID, bodyFontValue);

        // Push the resolved header and body font families into every preview theme root.
        previewTargets.forEach((target) => {
            target.style.setProperty("--strich-preview-header-font", resolvedHeaderFont || "inherit");
            target.style.setProperty("--strich-preview-body-font", resolvedBodyFont || "inherit");
        });

        // These sizing settings are only applied when the browser accepts the raw value as valid CSS.
        // Read the base font size setting so the preview can update its global text scale.
        const baseFontRawValue = String(
            GetSettingValueByCode(
                formSettings,
                "style_font_base",
                GetSettingValueByCode(savedSettings, "style_font_base", "16px")
            )
        ).trim();

        // Apply the base font size only when the browser accepts it as valid CSS.
        if (IsLiveThemeCssSizeValue(baseFontRawValue)) {
            previewTargets.forEach((target) => {
                target.style.setProperty("--strich-preview-base-font-size", baseFontRawValue);
            });
        }

        // Read the spacing setting so the preview can update spacing-related CSS variables.
        const spacingRawValue = String(
            GetSettingValueByCode(
                formSettings,
                "style_spacing",
                GetSettingValueByCode(savedSettings, "style_spacing", "1rem")
            )
        ).trim();

        // Apply the spacing value only when the browser accepts it as valid CSS.
        if (IsLiveThemeCssSizeValue(spacingRawValue)) {
            previewTargets.forEach((target) => {
                target.style.setProperty("--strich-preview-spacing", spacingRawValue);
            });
        }

        // Read the border-radius setting so the preview can update rounded corners in real time.
        const borderRadiusRawValue = String(
            GetSettingValueByCode(
                formSettings,
                "style_border_radius",
                GetSettingValueByCode(savedSettings, "style_border_radius", "12px")
            )
        ).trim();

        // Apply the border-radius value only when the browser accepts it as valid CSS.
        if (IsLiveThemeCssSizeValue(borderRadiusRawValue)) {
            previewTargets.forEach((target) => {
                target.style.setProperty("--strich-preview-border-radius", borderRadiusRawValue);
            });
        }

        // Update the preview logo directly if a valid URL is present.
        const brandLogoUrl = String(
            GetSettingValueByCode(
                formSettings,
                "style_brand_logo_url",
                GetSettingValueByCode(savedSettings, "style_brand_logo_url", "")
            )
        ).trim();

        // Swap the visible preview logos only when the current value is a valid web URL.
        if (brandLogoUrl && IsLiveThemeUrlValue(brandLogoUrl)) {
            previewDocument
                .querySelectorAll(".strich_podinline_storefront_header img, .strich_podinline_storefront_intro img, .pod-floating-trigger img")
                .forEach((imageEl) => {
                    // Point each preview logo image at the current live logo URL.
                    imageEl.src = brandLogoUrl;
                });
        }

        // Keep the hosted storefront header logo position in sync with the current draft setting.
        const brandLogoAlignment = String(
            GetSettingValueByCode(
                formSettings,
                "style_brand_logo_alignment",
                GetSettingValueByCode(savedSettings, "style_brand_logo_alignment", "centre")
            )
        ).trim().toLowerCase() === "left" ? "left" : "centre";

        previewDocument.querySelectorAll(".store-header").forEach((headerEl) => {
            headerEl.setAttribute("data-logo-alignment", brandLogoAlignment);
        });

        // Keep optional visual effects live without saving or reloading the hosted preview.
        Object.entries(LIVE_THEME_EFFECT_VARIABLE_MAP).forEach(([settingCode, cssVariable]) => {
            const isEnabled = String(
                GetSettingValueByCode(
                    formSettings,
                    settingCode,
                    GetSettingValueByCode(savedSettings, settingCode, "N")
                )
            ).trim().toUpperCase() === "Y";
            const cssValue = isEnabled ? "0.95" : "0";

            previewTargets.forEach((target) => {
                target.style.setProperty(cssVariable, cssValue);
            });
            liveThemeCssVariables[cssVariable] = cssValue;
        });

        // Let the preview panel background be controlled independently from the storefront theme itself.
        // Read the draft preview background so the portal frame can be themed independently.
        const previewBackgroundRawValue = String(
            GetSettingValueByCode(formSettings, "pod_inline_preview_background", "")
        ).trim();

        // Read the saved preview background for fallback when there is no valid draft value.
        const savedPreviewBackground = String(
            GetSettingValueByCode(savedSettings, "pod_inline_preview_background", "#ffffff")
        ).trim();

        // Prefer the current draft background when valid, otherwise fall back to the saved background or white.
        const previewBackgroundValue = previewBackgroundRawValue
            ? (isSupportedLivePreviewCssValue("background", previewBackgroundRawValue) ? previewBackgroundRawValue : "")
            : (isSupportedLivePreviewCssValue("background", savedPreviewBackground) ? savedPreviewBackground : "#ffffff");

        // Push the preview-stage background into every known preview theme root when a valid value exists.
        if (previewBackgroundValue) {
            previewTargets.forEach((target) => {
                target.style.background = previewBackgroundValue;
            });
        }

        // Keep editable storefront copy and floating button copy aligned in real time.
        SyncLivePreviewStorefrontText(previewDocument, {
            title: GetSettingValueByCode(
                formSettings,
                "pod_inline_title",
                GetSettingValueByCode(savedSettings, "pod_inline_title", "")
            ),
            subtitle: GetSettingValueByCode(
                formSettings,
                "pod_inline_subtitle",
                GetSettingValueByCode(savedSettings, "pod_inline_subtitle", "")
            ),
        });

        SyncLivePreviewFloatingButtonText(
            previewDocument,
            GetSettingValueByCode(
                formSettings,
                "pod_inline_floating_button_text",
                GetSettingValueByCode(savedSettings, "pod_inline_floating_button_text", "")
            )
        );

        // The cart shell has its own runtime updater, so feed it the merged setting snapshot too.
        if (typeof previewWindow?.strich?.cart?.updateSettings === "function") {
            // Let the cart runtime replace its settings snapshot in one call when that updater exists.
            previewWindow.strich.cart.updateSettings(mergedPreviewSettings, {
                replace: true,
                store_alias: previewAlias,
                cssVariables: liveThemeCssVariables,
            });
        }

        // Send the complete draft directly to the hosted storefront frames as well.
        // This keeps structural settings such as Top Menu live even when the cart
        // bridge is unavailable or was installed after the editor's first update.
        if (typeof previewWindow?.strich?.pod_inline?.__postHostedPreviewSettings === "function") {
            previewWindow.strich.pod_inline.__postHostedPreviewSettings(mergedPreviewSettings);
        }

    }, [
        addingNewConnection,
        formSettings,
        isOpen,
        podInlineStatus,
        previewAlias,
        previewFrameRef,
        savedSettings,
        showPodInlinePreview,
    ]);



    // Push the rest of the POD Inline runtime settings into the existing iframe instance. We skip this when the floating mode itself has changed because that path needs a full rebuild.
    useEffect(() => {

        // Stop here until the preview is open, usable, and associated with an existing saved store.
        if (!isOpen || addingNewConnection || !showPodInlinePreview) {
            return;
        }

        // Read the active iframe document so fallback DOM updates can be applied when needed.
        const previewDocument = previewFrameRef.current?.contentDocument;
        // Read the active iframe window so the richer preview runtime updater can be used when available.
        const previewWindow = previewFrameRef.current?.contentWindow;
        // Read the saved floating-mode value because a change here requires a full preview rebuild.
        const savedFloatingValue = GetSettingValueByCode(savedSettings, "pod_inline_floating", "N");
        // Read the current floating-mode value so we can decide whether an in-place update is safe.
        const currentFloatingValue = GetSettingValueByCode(
            formSettings,
            "pod_inline_floating",
            savedFloatingValue
        );

        // Switching floating mode changes structural markup, so let the full iframe rebuild path handle that instead.
        if (currentFloatingValue !== savedFloatingValue) {
            return;
        }

        // Build the exact payload shape expected by the inline preview runtime.
        const podInlinePreviewSettings = {
            // Force the live preview to use the portal settings template regardless of the form value.
            pod_inline_template: "portalsettings",
            // Read the live storefront title value for the preview runtime payload.
            pod_inline_title: GetSettingValueByCode(
                formSettings,
                "pod_inline_title",
                GetSettingValueByCode(savedSettings, "pod_inline_title", "")
            ),
            // Read the live storefront subtitle value for the preview runtime payload.
            pod_inline_subtitle: GetSettingValueByCode(
                formSettings,
                "pod_inline_subtitle",
                GetSettingValueByCode(savedSettings, "pod_inline_subtitle", "")
            ),
            // Read whether the product count should be shown in the live preview.
            pod_inline_product_count: GetSettingValueByCode(
                formSettings,
                "pod_inline_product_count",
                GetSettingValueByCode(savedSettings, "pod_inline_product_count", "N")
            ),
            // Read the current product alignment setting for the preview runtime payload.
            pod_inline_products_position: GetSettingValueByCode(
                formSettings,
                "pod_inline_products_position",
                GetSettingValueByCode(savedSettings, "pod_inline_products_position", "left")
            ),
            // Read the raw view-style string and trim it for the preview runtime payload.
            pod_inline_view_style: String(
                GetSettingValueByCode(
                    formSettings,
                    "pod_inline_view_style",
                    GetSettingValueByCode(savedSettings, "pod_inline_view_style", "")
                ) || ""
            ).trim(),
            // Read the current fit/fill setting for the preview runtime payload.
            pod_inline_fit_fill: GetSettingValueByCode(
                formSettings,
                "pod_inline_fit_fill",
                GetSettingValueByCode(savedSettings, "pod_inline_fit_fill", "fit")
            ),
            // Read whether the fit/fill toggle should be shown in the live preview.
            pod_inline_fit_fill_toggle: GetSettingValueByCode(
                formSettings,
                "pod_inline_fit_fill_toggle",
                GetSettingValueByCode(savedSettings, "pod_inline_fit_fill_toggle", "Y")
            ),
            // Read whether the top gallery should be enabled in the preview runtime payload.
            pod_inline_top_gallery: GetSettingValueByCode(
                formSettings,
                "pod_inline_top_gallery",
                GetSettingValueByCode(savedSettings, "pod_inline_top_gallery", "N")
            ),
            // Read whether image-position adjustment should be enabled in the preview runtime payload.
            pod_inline_adjust: GetSettingValueByCode(
                formSettings,
                "pod_inline_adjust",
                GetSettingValueByCode(savedSettings, "pod_inline_adjust", "N")
            ),
            // Reuse the current floating-mode value in the preview runtime payload.
            pod_inline_floating: GetSettingValueByCode(
                formSettings,
                "pod_inline_floating",
                savedFloatingValue
            ),
            // Read and trim the floating button text for the preview runtime payload.
            pod_inline_floating_button_text: String(
                GetSettingValueByCode(
                    formSettings,
                    "pod_inline_floating_button_text",
                    GetSettingValueByCode(savedSettings, "pod_inline_floating_button_text", "")
                ) || ""
            ).trim(),
            // Read and trim the floating button left-offset value for the preview runtime payload.
            pod_inline_floating_button_left: String(
                GetSettingValueByCode(
                    formSettings,
                    "pod_inline_floating_button_left",
                    GetSettingValueByCode(savedSettings, "pod_inline_floating_button_left", "")
                ) || ""
            ).trim(),
            // Read and trim the floating button top-offset value for the preview runtime payload.
            pod_inline_floating_button_top: String(
                GetSettingValueByCode(
                    formSettings,
                    "pod_inline_floating_button_top",
                    GetSettingValueByCode(savedSettings, "pod_inline_floating_button_top", "")
                ) || ""
            ).trim(),
            // Read whether the country/currency dropdown should be shown in the preview runtime payload.
            pod_inline_country_currency_dropdown: GetSettingValueByCode(
                formSettings,
                "pod_inline_country_currency_dropdown",
                GetSettingValueByCode(savedSettings, "pod_inline_country_currency_dropdown", "Y")
            ),
            // Read whether child products should be separated in the preview runtime payload.
            pod_inline_separate_child_products: GetSettingValueByCode(
                formSettings,
                "pod_inline_separate_child_products",
                GetSettingValueByCode(savedSettings, "pod_inline_separate_child_products", "N")
            ),
        };

        // Theme-only changes must not rerender the product runtime. Compare only the POD Inline settings handled by this effect.
        const podInlineRuntimeSettingsKey = JSON.stringify(podInlinePreviewSettings);

        if (lastPodInlineRuntimeSettingsKeyRef.current === podInlineRuntimeSettingsKey) {
            return;
        }

        if (typeof previewWindow?.strich?.pod_inline?.updatePreviewStoreSettings === "function") {

            // Prefer the runtime updater when available because it can rerender products in place without rebuilding.
            lastPodInlineRuntimeSettingsKeyRef.current = podInlineRuntimeSettingsKey;

            void Promise.resolve(
                previewWindow.strich.pod_inline.updatePreviewStoreSettings(podInlinePreviewSettings, {
                    // Ask the runtime to rerender products only when this POD Inline-specific payload changed.
                    rerenderProducts: true,
                })
            ).catch(() => {
                // Allow the same batch to retry if the runtime was still settling when this update was attempted.
                if (lastPodInlineRuntimeSettingsKeyRef.current === podInlineRuntimeSettingsKey) {
                    lastPodInlineRuntimeSettingsKeyRef.current = "";
                }
            });
            return;
        }

        // Older preview builds fall back to the minimal in-place DOM helpers we can safely control ourselves.
        ApplyLivePreviewFitFill(previewDocument, podInlinePreviewSettings.pod_inline_fit_fill);
        ApplyLivePreviewFitFillToggleVisibility(previewDocument, podInlinePreviewSettings.pod_inline_fit_fill_toggle);
        lastPodInlineRuntimeSettingsKeyRef.current = podInlineRuntimeSettingsKey;

    }, [
        addingNewConnection,
        formSettings,
        isOpen,
        podInlineStatus,
        previewFrameRef,
        savedSettings,
        showPodInlinePreview,
    ]);



    // Non-STRICH connections do not render the inline preview/settings UI at all.
    if (!showPodInlinePreview) {
        return null;
    }



    return (

        <div className="card connection-popup-preview-card">

            {/* Render the card header for the whole preview/settings experience. */}
            <div className="card__header">

                <h3>Store Styling</h3>

            </div>

            <div className="card__content">

                {/* New unsaved stores cannot render the live iframe until the store record exists. */}
                {addingNewConnection &&

                    <p>Save the store to view the inline store preview.</p>

                }

                {/* Existing stores still need a saved alias before the preview hook can build the iframe. */}
                {!addingNewConnection && !previewAlias &&

                    <p>Save the store to view the inline store preview.</p>

                }

                {/* Once the store exists and has an alias, show the editable sidebar and live preview stage. */}
                {!addingNewConnection && previewAlias &&

                    <div className="connection-popup-preview-layout">

                        {/* The left column is the editable preview-settings sidebar. */}
                        <aside className="store-theme-sidebar">

                            <div className="store-theme-sidebar__scroll">

                                <div className="store-theme-sidebar__tabs" role="tablist" aria-label="Inline store preview settings sections">

                                    {previewSidebarTabs.map((tab) => {

                                        // Highlight whichever high-level tab is currently selected.
                                        const isActiveTab = activePreviewSidebarTab === tab.key;

                                        return (

                                            <CreateButton
                                                title={tab.title}
                                                key={tab.key}
                                                type="button"
                                                size="s"
                                                fullWidth={true}
                                                invert={isActiveTab ? false : true}
                                                onClick={() => {   
                                                    setActivePreviewSidebarTab(tab.key);
                                                }}
                                                aria-selected={isActiveTab}
                                                role="tab"
                                            />

                                        );

                                    })}

                                </div>

                                {previewSidebarTabs
                                    .filter((tab) => tab.key === activePreviewSidebarTab)
                                    .map((activeTab) => (

                                        /* Render only the currently active tab panel so the sidebar stays focused. */
                                        <div key={activeTab.key} className="store-theme-sidebar__panel">

                                            <div className="store-theme-sidebar__sections">

                                                {activeTab.sections.map((sidebarSection) => (

                                                    /* Each section can render an optional heading plus one or more accordion groups. */
                                                    <div key={sidebarSection.key} className="store-theme-sidebar__sectionGroup">

                                                        {(sidebarSection.title || sidebarSection.description) &&

                                                            <div className="store-theme-sidebar__sectionHeader">

                                                                {sidebarSection.title &&

                                                                    <h5>{sidebarSection.title}</h5>

                                                                }

                                                                {sidebarSection.description &&

                                                                    <p>{sidebarSection.description}</p>
                                                                    
                                                                }

                                                            </div>
                                                        }

                                                        {sidebarSection.groups.map((group) => {

                                                            // Track whether this accordion group is currently expanded.
                                                            const isSectionOpen = !!openPreviewSidebarSections[group.key];

                                                            // Only reveal the floating button sub-fields when floating products are enabled.
                                                            const floatingProductsEnabled = GetSettingValueByCode(
                                                                formSettings,
                                                                "pod_inline_floating",
                                                                GetSettingValueByCode(savedSettings, "pod_inline_floating", "N")
                                                            ) === "Y";

                                                            const visibleFields = group.fields.filter((setting) => {

                                                                if (!POD_INLINE_FLOATING_SUB_SETTING_CODES.includes(setting.code)) {

                                                                    return true;

                                                                }

                                                                // Hide the floating button child controls until the parent floating setting is enabled.
                                                                return floatingProductsEnabled;

                                                            });

                                                            // Keep the floating button child settings grouped together so they can render beneath the parent toggle.
                                                            const floatingSubFields = visibleFields.filter((setting) => POD_INLINE_FLOATING_SUB_SETTING_CODES.includes(setting.code));

                                                            // Palette and surface groups render matching background/text settings as one preview card.
                                                            const groupedColourPairs = GROUPED_COLOUR_PAIRS[group.key] || [];

                                                            // The Accent controls are optional. Keep their saved values, but only expose them
                                                            // when a separate accent colour has been enabled.
                                                            const accentColourToggleSetting = group.key === "palette"
                                                                ? visibleFields.find((setting) => setting.code === "style_use_accent_colour")
                                                                : null;
                                                            const usesAccentColour = String(
                                                                accentColourToggleSetting?.value ?? accentColourToggleSetting?.defaultValue ?? "N"
                                                            ).trim().toUpperCase() === "Y";

                                                            return (

                                                                <section key={group.key} className="store-theme-sidebar__section">

                                                                    {/* The section toggle opens/closes one setting group inside the sidebar. */}
                                                                    <button
                                                                        type="button"
                                                                        className={`store-theme-sidebar__sectionToggle ${isSectionOpen ? "store-theme-sidebar__sectionToggle--open" : "store-theme-sidebar__sectionToggle--closed"}`}
                                                                        onClick={() => {

                                                                            setOpenPreviewSidebarSections((previousSections) => ({

                                                                                ...previousSections,
                                                                                [group.key]: !previousSections[group.key],

                                                                            }));

                                                                        }}
                                                                        aria-expanded={isSectionOpen}
                                                                        >

                                                                            <span>{group.label}</span>

                                                                            <div className="pod-pricing-page__section-collapse-container">

                                                                                {/* This is the collapse icon */}
                                                                                <span  
                                                                                    className="pod-pricing-page__icon-button"
                                                                                    title={isSectionOpen ? "Collapse Group" : "Expand Group"}
                                                                                >
                                                                                    {isSectionOpen ? "-" : "+"}
                                                                                </span>

                                                                            </div>

                                                                    </button>

                                                                    {isSectionOpen &&

                                                                        <div className="store-theme-sidebar__sectionBody">

                                                                            <div className="store-theme-ribbon__groupFields">

                                                                                {groupedColourPairs.length > 0 &&

                                                                                    <div className="store-theme-ribbon__colourPairs">

                                                                                        {groupedColourPairs.map((pair) => {

                                                                                            const backgroundSetting = visibleFields.find((setting) => setting.code === pair.backgroundCode);
                                                                                            const textSetting = visibleFields.find((setting) => setting.code === pair.textCode);
                                                                                            const borderSetting = pair.borderCode
                                                                                                ? visibleFields.find((setting) => setting.code === pair.borderCode)
                                                                                                : null;
                                                                                            const focusBorderSetting = pair.focusBorderCode
                                                                                                ? visibleFields.find((setting) => setting.code === pair.focusBorderCode)
                                                                                                : null;

                                                                                            if (!backgroundSetting || !textSetting) {
                                                                                                return null;
                                                                                            }

                                                                                            const previewBackground = isSupportedLivePreviewCssValue("color", backgroundSetting.value)
                                                                                                ? backgroundSetting.value
                                                                                                : "transparent";
                                                                                            const previewText = isSupportedLivePreviewCssValue("color", textSetting.value)
                                                                                                ? textSetting.value
                                                                                                : "inherit";
                                                                                            const previewBorder = borderSetting && isSupportedLivePreviewCssValue("color", borderSetting.value)
                                                                                                ? borderSetting.value
                                                                                                : undefined;
                                                                                            const previewFocusBorder = focusBorderSetting && isSupportedLivePreviewCssValue("color", focusBorderSetting.value)
                                                                                                ? focusBorderSetting.value
                                                                                                : undefined;
                                                                                            const groupedSettings = [backgroundSetting, textSetting, borderSetting, focusBorderSetting].filter(Boolean);
                                                                                            const getGroupedSettingLabel = (setting) => setting.code === pair.backgroundCode
                                                                                                ? "Background"
                                                                                                : setting.code === pair.textCode
                                                                                                    ? "Text"
                                                                                                    : setting.code === pair.focusBorderCode
                                                                                                        ? "Focus Border"
                                                                                                        : "Border";
                                                                                            const pairIsActive = groupedSettings.some((setting) => setting.code === openColourPickerCode);

                                                                                            return (

                                                                                                <Fragment key={pair.key}>

                                                                                                    {pair.key === "accent" && accentColourToggleSetting && (

                                                                                                        <div className="store-theme-ribbon__field store-theme-ribbon__field--toggle">

                                                                                                            <div className="store-theme-ribbon__labelRow">

                                                                                                                <span className="store-theme-ribbon__label">

                                                                                                                    {accentColourToggleSetting.shortLabel}

                                                                                                                </span>

                                                                                                                <InfoButton
                                                                                                                    shortLabel={accentColourToggleSetting.shortLabel}
                                                                                                                    description={accentColourToggleSetting.description}
                                                                                                                />

                                                                                                            </div>

                                                                                                            <div className="store-theme-ribbon__inputWrap">

                                                                                                                <ToggleSwitch
                                                                                                                    name={accentColourToggleSetting.code}
                                                                                                                    value={usesAccentColour}
                                                                                                                    label=""
                                                                                                                    labelPosition="right"
                                                                                                                    size="s"
                                                                                                                    onChange={(event) => {

                                                                                                                        createSettingValueChangeHandler(
                                                                                                                            accentColourToggleSetting.type,
                                                                                                                            accentColourToggleSetting.code,
                                                                                                                            accentColourToggleSetting.name,
                                                                                                                            accentColourToggleSetting.description,
                                                                                                                            accentColourToggleSetting.value_type
                                                                                                                        )(event.target.value ? "Y" : "N");

                                                                                                                    }}
                                                                                                                />

                                                                                                            </div>

                                                                                                        </div>

                                                                                                    )}

                                                                                                    {!(pair.key === "accent" && !usesAccentColour) && (

                                                                                                        <div
                                                                                                            className={`store-theme-ribbon__colourPair ${pairIsActive ? "store-theme-ribbon__colourPair--active" : ""}`}
                                                                                                        >

                                                                                                    <div
                                                                                                        className="store-theme-ribbon__colourPairPreview"
                                                                                                        style={{
                                                                                                            backgroundColor: previewBackground,
                                                                                                            color: previewText,
                                                                                                            borderColor: previewBorder,
                                                                                                            boxShadow: previewFocusBorder
                                                                                                                ? `inset 0 0 0 1px rgba(255, 255, 255, 0.08), 0 0 0 2px ${previewFocusBorder}`
                                                                                                                : undefined,
                                                                                                        }}
                                                                                                    >
                                                                                                        <span>{pair.label}</span>

                                                                                                        <span className="store-theme-ribbon__colourPairInfo">
                                                                                                            <InfoButton
                                                                                                                shortLabel={pair.label}
                                                                                                                description={(
                                                                                                                    <span className="store-theme-ribbon__colourPairHelp">
                                                                                                                        {groupedSettings.map((setting) => (
                                                                                                                            <span key={setting.code}>
                                                                                                                                <strong>{getGroupedSettingLabel(setting)}:</strong>{" "}
                                                                                                                                {setting.description || setting.name}
                                                                                                                            </span>
                                                                                                                        ))}
                                                                                                                    </span>
                                                                                                                )}
                                                                                                            />
                                                                                                        </span>
                                                                                                    </div>

                                                                                                    <div className={`store-theme-ribbon__colourPairControls${groupedSettings.length === 4 ? " store-theme-ribbon__colourPairControls--four" : borderSetting ? " store-theme-ribbon__colourPairControls--three" : ""}`}>

                                                                                                        {groupedSettings.map((setting) => (

                                                                                                            <div key={setting.code} className="store-theme-ribbon__colourPairControl">

                                                                                                                <ColourPickerSmall
                                                                                                                    label=""
                                                                                                                    title={setting.name}
                                                                                                                    size="l"
                                                                                                                    value={isSupportedLivePreviewCssValue("color", setting.value) ? setting.value : null}
                                                                                                                    onOpen={() => {
                                                                                                                        setOpenColourPickerCode(setting.code);
                                                                                                                    }}
                                                                                                                    onClose={() => {
                                                                                                                        setOpenColourPickerCode(null);
                                                                                                                    }}
                                                                                                                    onChange={createSettingValueChangeHandler(
                                                                                                                        setting.type,
                                                                                                                        setting.code,
                                                                                                                        setting.name,
                                                                                                                        setting.description,
                                                                                                                        setting.value_type
                                                                                                                    )}
                                                                                                                    allowNullOrTransparent={true}
                                                                                                                    setClearColourAsTransparent={true}
                                                                                                                    allowAlpha={true}
                                                                                                                />

                                                                                                                <div className="store-theme-ribbon__colourPairLabel">

                                                                                                                    <span>{getGroupedSettingLabel(setting)}</span>

                                                                                                                </div>

                                                                                                            </div>

                                                                                                        ))}

                                                                                                    </div>

                                                                                                        </div>

                                                                                                    )}

                                                                                                </Fragment>

                                                                                            );

                                                                                        })}

                                                                                    </div>

                                                                                }

                                                                                {visibleFields
                                                                                    .filter((setting) => !POD_INLINE_FLOATING_SUB_SETTING_CODES.includes(setting.code))
                                                                                    .filter((setting) => groupedColourPairs.length === 0 || !GROUPED_COLOUR_SETTING_CODES.has(setting.code))
                                                                                    .filter((setting) => setting.code !== "style_use_accent_colour")
                                                                                    .map((setting) => {

                                                                                    // The floating products toggle owns a small nested group of related inputs.
                                                                                    const shouldRenderFloatingSubSettings = setting.code === "pod_inline_floating" && floatingSubFields.length > 0;
                                                                                    // Parse the current ordered page selection once for the Top Menu control.
                                                                                    const selectedTopMenuPageSlugs = setting.inputMode === "page-multi-select"
                                                                                        ? GetTopMenuPageSlugs(setting.value ?? setting.defaultValue)
                                                                                        : [];

                                                                                    return (

                                                                                        <Fragment key={setting.code}>

                                                                                            <div className={`store-theme-ribbon__field store-theme-ribbon__field--${setting.inputMode} 
                                                                                                    ${openColourPickerCode === setting.code ? "store-theme-ribbon__field--active" : ""}
                                                                                                    ${openFieldCode  === setting.code ? "store-theme-ribbon__field--active" : ""}
                                                                                                `}>

                                                                                                {setting.inputMode === "colour" ? (

                                                                                                        // Colour rows use the shared small colour picker rather than a plain text input.
                                                                                                        <div className="store-theme-ribbon__colourPickerRow">

                                                                                                            <ColourPickerSmall
                                                                                                                label=""
                                                                                                                title={setting.name}
                                                                                                                size="l"
                                                                                                                value={isSupportedLivePreviewCssValue("color", setting.value) ? setting.value : null}
                                                                                                                
                                                                                                                onOpen={() => {

                                                                                                                    setOpenColourPickerCode(setting.code);

                                                                                                                }}
                                                                                                                onClose={() => {

                                                                                                                    setOpenColourPickerCode(null);

                                                                                                                }}
                                                                                                                onChange={createSettingValueChangeHandler(
                                                                                                                    setting.type,
                                                                                                                    setting.code,
                                                                                                                    setting.name,
                                                                                                                    setting.description,
                                                                                                                    setting.value_type
                                                                                                                )}
                                                                                                                allowNullOrTransparent={true}
                                                                                                                setClearColourAsTransparent={true}
                                                                                                                allowAlpha={true}
                                                                                                            />

                                                                                                            <div className="store-theme-ribbon__labelRow store-theme-ribbon__labelRow--inline">

                                                                                                                <span className="store-theme-ribbon__label store-theme-ribbon__label--inline">

                                                                                                                    {setting.shortLabel}

                                                                                                                </span>

                                                                                                                <InfoButton
                                                                                                                    shortLabel={setting.shortLabel}
                                                                                                                    description={setting.description}
                                                                                                                />

                                                                                                            </div>

                                                                                                        </div>

                                                                                                    )

                                                                                                    : (

                                                                                                        <>
                                                                                                            {/* Text and select rows share the same label/input layout. */}
                                                                                                            <div className="store-theme-ribbon__labelRow">

                                                                                                                <span className="store-theme-ribbon__label">

                                                                                                                    {setting.shortLabel}

                                                                                                                </span>

                                                                                                                <InfoButton
                                                                                                                    shortLabel={setting.shortLabel}
                                                                                                                    description={setting.description}
                                                                                                                />


                                                                                                            </div>

                                                                                                            <div className="store-theme-ribbon__inputWrap">

                                                                                                                {setting.inputMode === "font" ? (

                                                                                                                        <GoogleFontPicker
                                                                                                                            value={setting.value ?? setting.defaultValue ?? ""}
                                                                                                                            placeholder={setting.placeholder || ""}
                                                                                                                            onOpen={() => {

                                                                                                                               setOpenFieldCode(setting.code);
                                                                                                                               minimiseCartPreviewForFontEditing();

                                                                                                                           }}
                                                                                                                            onClose={() => {

                                                                                                                                setOpenFieldCode(null);

                                                                                                                            }}
                                                                                                                            onChange={createSettingValueChangeHandler(
                                                                                                                                setting.type,
                                                                                                                                setting.code,
                                                                                                                                setting.name,
                                                                                                                                setting.description,
                                                                                                                                setting.value_type
                                                                                                                            )}
                                                                                                                        />
                                                                                                                    )

                                                                                                                    : setting.inputMode === "toggle"

                                                                                                                    ? (

                                                                                                                        <ToggleSwitch
                                                                                                                            name={setting.code}
                                                                                                                            value={String(setting.value ?? setting.defaultValue ?? "N").trim().toUpperCase() === "Y"}
                                                                                                                            label=""
                                                                                                                            labelPosition="right"
                                                                                                                            size="s"
                                                                                                                            onChange={(event) => {

                                                                                                                                createSettingValueChangeHandler(
                                                                                                                                    setting.type,
                                                                                                                                    setting.code,
                                                                                                                                    setting.name,
                                                                                                                                    setting.description,
                                                                                                                                    setting.value_type
                                                                                                                                )(event.target.value ? "Y" : "N");

                                                                                                                            }}
                                                                                                                        />

                                                                                                                    )

                                                                                                                    : setting.inputMode === "select"

                                                                                                                    ? (

                                                                                                                        <select
                                                                                                                            className="store-theme-ribbon__input"
                                                                                                                            value={setting.value ?? setting.defaultValue ?? ""}
                                                                                                                            onChange={createSettingsChangeHandler(
                                                                                                                                setting.type,
                                                                                                                                setting.code,
                                                                                                                                setting.name,
                                                                                                                                setting.description,
                                                                                                                                setting.value_type
                                                                                                                            )}
                                                                                                                        >

                                                                                                                            {(setting.options || []).map((option) => (

                                                                                                                                <option key={option.value} value={option.value}>

                                                                                                                                    {option.label}

                                                                                                                                </option>

                                                                                                                            ))}

                                                                                                                        </select>

                                                                                                                    )

                                                                                                                    : setting.inputMode === "page-multi-select"

                                                                                                                   ? (
                                                                                                                       <div className="store-theme-ribbon__pageChoices">
                                                                                                                           {availableTopMenuPages.length > 0 ? (
                                                                                                                               <>
                                                                                                                                   <div className="store-theme-ribbon__pageChoiceGrid">
                                                                                                                                        {[...selectedTopMenuPageSlugs
                                                                                                                                            .map((slug) => availableTopMenuPages.find((page) => page.slug === slug))
                                                                                                                                            .filter(Boolean),
                                                                                                                                            ...availableTopMenuPages.filter((page) => !selectedTopMenuPageSlugs.includes(page.slug))
                                                                                                                                        ].map((page) => {
                                                                                                                                            const isSelected = selectedTopMenuPageSlugs.includes(page.slug);
                                                                                                                                            const selectionLimitReached = selectedTopMenuPageSlugs.length >= 4 && !isSelected;

                                                                                                                                            return (
                                                                                                                                                <button
                                                                                                                                                    key={page.slug}
                                                                                                                                                    type="button"
                                                                                                                                                    className={`store-theme-ribbon__pageChoice${isSelected ? " store-theme-ribbon__pageChoice--selected" : ""}${draggedTopMenuPageSlug === page.slug ? " store-theme-ribbon__pageChoice--dragging" : ""}${topMenuDropTargetSlug === page.slug ? " store-theme-ribbon__pageChoice--dropTarget" : ""}`}
                                                                                                                                                    aria-pressed={isSelected}
                                                                                                                                                    disabled={selectionLimitReached}
                                                                                                                                                    draggable={isSelected}
                                                                                                                                                    onDragStart={(event) => {
                                                                                                                                                        if (!isSelected) {
                                                                                                                                                            return;
                                                                                                                                                        }

                                                                                                                                                        draggedTopMenuPageSlugRef.current = page.slug;
                                                                                                                                                        setDraggedTopMenuPageSlug(page.slug);
                                                                                                                                                        setTopMenuDropTargetSlug("");
                                                                                                                                                        event.dataTransfer.effectAllowed = "move";
                                                                                                                                                        event.dataTransfer.setData("text/plain", page.slug);
                                                                                                                                                    }}
                                                                                                                                                    onDragOver={(event) => {
                                                                                                                                                        if (isSelected && draggedTopMenuPageSlugRef.current && draggedTopMenuPageSlugRef.current !== page.slug) {
                                                                                                                                                            event.preventDefault();
                                                                                                                                                            event.dataTransfer.dropEffect = "move";
                                                                                                                                                        }
                                                                                                                                                    }}
                                                                                                                                                    onDragEnter={() => {
                                                                                                                                                        if (isSelected && draggedTopMenuPageSlugRef.current && draggedTopMenuPageSlugRef.current !== page.slug) {
                                                                                                                                                            setTopMenuDropTargetSlug(page.slug);
                                                                                                                                                        }
                                                                                                                                                    }}
                                                                                                                                                    onDrop={(event) => {
                                                                                                                                                        const draggedSlug = draggedTopMenuPageSlugRef.current || event.dataTransfer.getData("text/plain");
                                                                                                                                                        draggedTopMenuPageSlugRef.current = "";
                                                                                                                                                        setDraggedTopMenuPageSlug("");
                                                                                                                                                        setTopMenuDropTargetSlug("");

                                                                                                                                                        if (!isSelected || !draggedSlug || draggedSlug === page.slug || !selectedTopMenuPageSlugs.includes(draggedSlug)) {
                                                                                                                                                            return;
                                                                                                                                                        }

                                                                                                                                                        event.preventDefault();
                                                                                                                                                        const sourceIndex = selectedTopMenuPageSlugs.indexOf(draggedSlug);
                                                                                                                                                        const targetIndex = selectedTopMenuPageSlugs.indexOf(page.slug);
                                                                                                                                                        const nextSlugs = [...selectedTopMenuPageSlugs];
                                                                                                                                                        const [movedSlug] = nextSlugs.splice(sourceIndex, 1);
                                                                                                                                                        nextSlugs.splice(targetIndex, 0, movedSlug);

                                                                                                                                                        createSettingValueChangeHandler(
                                                                                                                                                            setting.type,
                                                                                                                                                            setting.code,
                                                                                                                                                            setting.name,
                                                                                                                                                            setting.description,
                                                                                                                                                            setting.value_type
                                                                                                                                                        )(nextSlugs.join(","));
                                                                                                                                                    }}
                                                                                                                                                    onDragEnd={() => {
                                                                                                                                                        draggedTopMenuPageSlugRef.current = "";
                                                                                                                                                        lastTopMenuDragEndedAtRef.current = Date.now();
                                                                                                                                                        setDraggedTopMenuPageSlug("");
                                                                                                                                                        setTopMenuDropTargetSlug("");
                                                                                                                                                    }}
                                                                                                                                                    onClick={() => {
                                                                                                                                                        if (Date.now() - lastTopMenuDragEndedAtRef.current < 300) {
                                                                                                                                                            return;
                                                                                                                                                        }

                                                                                                                                                        const nextSlugs = isSelected
                                                                                                                                                            ? selectedTopMenuPageSlugs.filter((slug) => slug !== page.slug)
                                                                                                                                                            : [...selectedTopMenuPageSlugs, page.slug].slice(0, 4);

                                                                                                                                                        createSettingValueChangeHandler(
                                                                                                                                                            setting.type,
                                                                                                                                                            setting.code,
                                                                                                                                                            setting.name,
                                                                                                                                                            setting.description,
                                                                                                                                                            setting.value_type
                                                                                                                                                        )(nextSlugs.join(","));
                                                                                                                                                    }}
                                                                                                                                                >
                                                                                                                                                    <span className="store-theme-ribbon__pageChoiceMark" aria-hidden="true">{isSelected ? "✓" : "+"}</span>
                                                                                                                                                    <span className="store-theme-ribbon__pageChoiceLabel">{page.name}</span>
                                                                                                                                                    {isSelected && (
                                                                                                                                                        <span className="store-theme-ribbon__pageChoiceDragHandle" aria-hidden="true">
                                                                                                                                                            <svg viewBox="0 0 16 20" width="16" height="20" focusable="false">
                                                                                                                                                                <circle cx="5" cy="4" r="1.35" fill="currentColor" />
                                                                                                                                                                <circle cx="11" cy="4" r="1.35" fill="currentColor" />
                                                                                                                                                                <circle cx="5" cy="10" r="1.35" fill="currentColor" />
                                                                                                                                                                <circle cx="11" cy="10" r="1.35" fill="currentColor" />
                                                                                                                                                                <circle cx="5" cy="16" r="1.35" fill="currentColor" />
                                                                                                                                                                <circle cx="11" cy="16" r="1.35" fill="currentColor" />
                                                                                                                                                            </svg>
                                                                                                                                                        </span>
                                                                                                                                                    )}
                                                                                                                                                </button>
                                                                                                                                            );
                                                                                                                                        })}
                                                                                                                                    </div>
                                                                                                                                    <span className="store-theme-ribbon__pageChoiceCount">
                                                                                                                                        {selectedTopMenuPageSlugs.length} of 4 selected
                                                                                                                                    </span>
                                                                                                                                </>
                                                                                                                            ) : (
                                                                                                                                <span className="store-theme-ribbon__pageChoiceEmpty">
                                                                                                                                    Add a page in the Pages tab before choosing Top Menu links.
                                                                                                                                </span>
                                                                                                                            )}
                                                                                                                        </div>
                                                                                                                    )

                                                                                                                        : setting.inputMode === "image" ?

                                                                                                                            setting.value ? (

                                                                                                                                <div className="store-theme-ribbon__image-container">

                                                                                                                                    <img
                                                                                                                                        src={setting.value}
                                                                                                                                        alt={`${setting.name} preview`}
                                                                                                                                    />


                                                                                                                                    <CreateButton
                                                                                                                                        title="Replace"
                                                                                                                                        size="xs"
                                                                                                                                        invert={true}
                                                                                                                                        tooltip="Select a different image"
                                                                                                                                        className="store-theme-ribbon__replace-image-button"
                                                                                                                                        onClick={() => {

                                                                                                                                            // Clear the current image so the uploader becomes visible
                                                                                                                                            createSettingValueChangeHandler(
                                                                                                                                                setting.type,
                                                                                                                                                setting.code,
                                                                                                                                                setting.name,
                                                                                                                                                setting.description,
                                                                                                                                                setting.value_type
                                                                                                                                            )("");

                                                                                                                                        }}
                                                                                                                                    />

                                                                                                                                </div>

                                                                                                                            ) : (

                                                                                                                                <div className="store-theme-ribbon__image">

                                                                                                                                    <RenderImageUploader
                                                                                                                                        label={setting.name}
                                                                                                                                        selectedImage={setting.value || null}
                                                                                                                                        onImageChange={(payload) => {

                                                                                                                                            // Read the full uploaded image URL from the uploader payload
                                                                                                                                            const imageUrl = String(
                                                                                                                                                payload?.imageBucketUrl || ""
                                                                                                                                            ).trim();

                                                                                                                                            // Store the uploaded image URL in the setting
                                                                                                                                            createSettingValueChangeHandler(
                                                                                                                                                setting.type,
                                                                                                                                                setting.code,
                                                                                                                                                setting.name,
                                                                                                                                                setting.description,
                                                                                                                                                setting.value_type
                                                                                                                                            )(imageUrl);

                                                                                                                                        }}
                                                                                                                                    />

                                                                                                                                </div>

                                                                                                                            )

                                                                                                                    : setting.inputMode === "setting-message" ?

                                                                                                                    (
                                                                                                                                <div className="store-theme-ribbon__message">

                                                                                                                                    <p>{setting.placeholder}</p>

                                                                                                                                </div>

                                                                                                                    )

                                                                                                                    : setting.inputMode === "range" ? (

                                                                                                                        <div className="store-theme-ribbon__rangeControl">

                                                                                                                            <input
                                                                                                                                className="store-theme-ribbon__range"
                                                                                                                                type="range"
                                                                                                                                min={setting.min ?? 0}
                                                                                                                                max={setting.max ?? 2}
                                                                                                                                step={setting.step ?? 0.1}
                                                                                                                                value={GetSearchBorderRadiusSliderValue(setting.value ?? setting.defaultValue)}
                                                                                                                                onChange={(event) => {
                                                                                                                                    const nextValue = Number(event.target.value);
                                                                                                                                    createSettingValueChangeHandler(
                                                                                                                                        setting.type,
                                                                                                                                        setting.code,
                                                                                                                                        setting.name,
                                                                                                                                        setting.description,
                                                                                                                                        setting.value_type
                                                                                                                                    )(`${nextValue}${setting.unit || "rem"}`);
                                                                                                                                }}
                                                                                                                            />

                                                                                                                       </div>

                                                                                                                    )

                                                                                                                    : (

                                                                                                                        <input
                                                                                                                            className="store-theme-ribbon__input"
                                                                                                                            type="text"
                                                                                                                            value={setting.value ?? ""}
                                                                                                                            placeholder={setting.placeholder || ""}
                                                                                                                            onChange={createSettingsChangeHandler(
                                                                                                                                setting.type,
                                                                                                                                setting.code,
                                                                                                                                setting.name,
                                                                                                                                setting.description,
                                                                                                                                setting.value_type
                                                                                                                            )}
                                                                                                                        />

                                                                                                                    )}

                                                                                                            </div>

                                                                                                        </>

                                                                                                    )}

                                                                                            </div>

                                                                                            {shouldRenderFloatingSubSettings &&

                                                                                                <div className="store-theme-ribbon__subSettings">

                                                                                                    {/* Render the floating button text/position helpers directly under the parent toggle. */}
                                                                                                    {floatingSubFields.map((subSetting) => (

                                                                                                        <label
                                                                                                            key={subSetting.code}
                                                                                                            className={`store-theme-ribbon__field store-theme-ribbon__field--${subSetting.inputMode} store-theme-ribbon__field--subSetting`}
                                                                                                        >

                                                                                                            <div className="store-theme-ribbon__labelRow">

                                                                                                                <span className="store-theme-ribbon__label">

                                                                                                                    {subSetting.shortLabel}

                                                                                                                </span>

                                                                                                                <InfoButton
                                                                                                                    shortLabel={subSetting.shortLabel}
                                                                                                                    description={subSetting.description}
                                                                                                                />

                                                                                                            </div>

                                                                                                            <div className="store-theme-ribbon__inputWrap">

                                                                                                                {/* Floating sub-settings currently use plain text inputs for values like text and percentage offsets. */}
                                                                                                                <input
                                                                                                                    className="store-theme-ribbon__input"
                                                                                                                    type="text"
                                                                                                                    value={subSetting.value ?? ""}
                                                                                                                    placeholder={subSetting.placeholder || ""}
                                                                                                                    onChange={createSettingsChangeHandler(
                                                                                                                        subSetting.type,
                                                                                                                        subSetting.code,
                                                                                                                        subSetting.name,
                                                                                                                        subSetting.description,
                                                                                                                        subSetting.value_type
                                                                                                                    )}
                                                                                                                />

                                                                                                            </div>

                                                                                                        </label>

                                                                                                    ))}

                                                                                                </div>

                                                                                            }

                                                                                        </Fragment>

                                                                                    );

                                                                                })}

                                                                            </div>

                                                                        </div>

                                                                    }

                                                                </section>

                                                            );

                                                        })}

                                                    </div>

                                                ))}

                                            </div>

                                        </div>

                                    ))}

                            </div>

                        </aside>

                        {/* The right column is the live iframe preview itself. */}
                        <div className={`connection-popup-preview-stage${showPreviewLoadingState ? " connection-popup-preview-stage--loading" : ""}${["store-products", "store-product-page", "store-page"].includes(expandedPreviewPanel) ? " connection-popup-preview-stage--hosted-expanded" : ""}`}>

                            {/* Render portal-side controls that tell the iframe which preview panel to expand. */}
                            <div className="connection-popup-preview-toolbar">

                                <div className="connection-popup-preview-toolbarTabs" role="tablist" aria-label="Preview panels">

                                    {[
                                        { key: "view_all", label: "View All" },
                                        { key: "product", label: "Inline Products" },
                                        { key: "cart", label: "Cart" },
                                        { key: "checkout", label: "Checkout" },
                                        { key: "store-products", label: "Store Products" },
                                        { key: "store-product-page", label: "Store Product" },
                                        { key: "store-page", label: "Landing Page" },

                                        // Only include this tab if enabled
                                        ...(GetSettingValueByCode(formSettings, "pod_inline_floating", "N")
                                            === "Y"
                                                ? [{ key: "floating-position", label: "Floating Product" }]
                                                : []
                                        ),       

                                    ].map((panel) => {

                                        // Highlight whichever preview panel is currently expanded inside the iframe.
                                        const isActivePanel = expandedPreviewPanel === panel.key;
                                        
                                        return (

                                            <CreateButton
                                                title={panel.label}
                                                key={panel.key}
                                                size="s"
                                                invert={!isActivePanel}
                                                fullWidth={true}
                                                aria-pressed={isActivePanel}
                                                onClick={() => { 

                                                    if (panel.key === "view_all") {

                                                        sendPreviewPanelAction("close-expanded-panel");

                                                    } else {

                                                        sendPreviewPanelAction("expand-panel", panel.key);

                                                    }

                                                }}
                                            />

                                        );

                                    })}

                                </div>

                            </div>

                            {/* Show a blocking loading state until the current iframe instance has rendered once. */}
                            {showPreviewLoadingState &&

                                <div className="connection-popup-preview-status">

                                    <LoadingSpinner />

                                    <span>Loading inline store preview...</span>

                                </div>

                            }

                            {/* Render the live preview iframe once the hook has produced the current srcDoc. */}
                            {previewSrcDoc &&

                                <iframe
                                    key={previewKey}
                                    ref={previewFrameRef}
                                    title={`Inline store preview for ${previewAlias}`}
                                    className={`connection-popup-preview-frame${showPreviewLoadingState ? " connection-popup-preview-frame--hidden" : ""}`}
                                    srcDoc={previewSrcDoc}
                                />

                            }

                            {/* Surface iframe/bootstrap errors from the shared preview hook in the preview stage. */}
                            {podInlineStatus === "error" &&

                                <div className="connection-popup-preview-status connection-popup-preview-status--error">

                                    {podInlineError}

                                </div>

                            }

                        </div>

                    </div>
                }

            </div>

        </div>

    );

};

export default InlineStorePreviewSettings;
